import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../../contexts/ToastContext";
import { useRegisterUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectCanAccessFinance,
  selectAllowMultipleTherapists,
  selectIsAdmin,
  selectHasPermission,
  selectPreventTherapistDoubleBooking,
  selectBlockBookingOnAbsence,
  selectPunchCardsEnabled,
  selectIsEndMeetingSmsEnabled,
  selectTempCustomersEnabled,
  selectOnlineMeetingsEnabled,
  selectEnglishCommunicationEnabled,
  selectClinicSharesAllPatients,
} from "../../../store/slices/authSlice";
import {
  showGlobalLoader,
  hideGlobalLoader,
} from "../../../store/slices/uiSlice";
import {
  useCreateAppointmentMutation,
  useCreateRecurringAppointmentsMutation,
  useUpdateAppointmentMutation,
  useUpdateRecurringAppointmentsMutation,
  useDeleteAppointmentMutation,
  useDeleteRecurringAppointmentsMutation,
  useUncancelAppointmentMutation,
  useGetAppointmentQuery,
  useGetRecurringGroupAppointmentsQuery,
  useGetConfirmationLinksQuery,
  useDeactivateConfirmationLinkMutation,
  useCancelScheduledSmsMutation,
  useSendReminderNowMutation,
  useCreateConfirmationLinkMutation,
  useGetSmsSettingsQuery,
  useGetLastPatientAppointmentQuery,
  useCreateBlockedTimeMutation,
  RECURRING_FREQUENCY,
  SERIES_SLOT,
  calculateSeriesOccurrences,
} from "../../../store/services/calendarApi";
import { useGetCategoriesQuery } from "../../../store/services/tasksApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import { useUpdatePatientTherapistAssignmentsMutation } from "../../../store/services/patientsApi";
import { isAssignableTherapist } from "../../../config/permissionsRegistry";
import { useGetRoomsQuery } from "../../../store/services/roomsApi";
import { useGetPatientsQuery, patientsApi, PATIENTS_PICKER_PAGE_SIZE } from "../../../store/services/patientsApi";
import { useGetActivePunchCardQuery } from "../../../store/services/punchCardsApi";
import usePlanPermissions from "../../../hooks/usePlanPermissions";
import useAppointmentChecks from "../../../hooks/useAppointmentChecks";
import {
  getPatientTherapistIds,
  sendAppointmentSMS,
  formatHebrewDate,
  syncConfirmationLinkPhone,
  getTransactionByAppointment,
  subscribeToAppointmentSmsState,
  checkRoomAvailability as checkRoomAvailabilityFn,
  checkTherapistAvailability as checkTherapistAvailabilityFn,
  getAbsences,
  resolveRoomAvailability,
  generateVideoMeetLinkId,
} from "../../../firebase";
import { findBlockingAbsence, toIsraelDateStr, toIsraelHHMM } from "../../../utils/absenceUtils";
import useCancelAppointmentFlow from "../../dashboard/hooks/useCancelAppointmentFlow";
import { formatLocalDateString } from "../../../utils/dateUtils";
import { canSendManualReminder } from "../../../utils/appointmentDisplay";
import {
  buildSmsPreview,
  buildSeriesSmsMessage,
  buildConfirmationSmsText,
  getSchedulingFromPreview,
  applySmsScheduleGuards,
  SMS_SUPPRESS_REASON,
  formatSmsDate,
} from "../../../utils/appointmentSmsPreview";
import { getPatientLanguage } from "../../../utils/patientLanguage";
import { FREQUENCY_LABELS } from "../../../utils/smsTemplates";
import {
  resolveRate,
  resolveDuration,
  isPastDateTimeValue,
  validatePatient,
  validateSchedule,
  validateDetails,
  validateBlock,
  validateExternalConflicts,
} from "../../../utils/appointmentFormRules";

export const TEMP_CUSTOMER_OPTION = "__temp_customer__";
// Blocked time (חסימת זמן פרטי) — permanent patient-list option, no flag gate.
export const BLOCK_TIME_OPTION = "__blocked_time__";

export const WIZARD_STEPS = {
  PATIENT: 1,
  SCHEDULE: 2,
  DETAILS: 3,
  SUMMARY: 4,
};

/**
 * All state + business logic of the appointment creation wizard.
 * Create-only in Phase 1 — edit mode stays on the classic AppointmentModal.
 * Mutation arguments are byte-identical to AppointmentModal's create flow.
 */
export default function useAppointmentForm({
  appointment = null,
  prefilledDate,
  prefilledDateOnly = false,
  patients: patientsProp,
  onClose,
  targetTherapistId = null,
  targetTherapistName = null,
  scheduledByAdminId = null,
  scheduledByAdminName = null,
  preSelectedRoomId = null,
  onSuccess = null,
}) {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const isEditing = !!appointment;

  const clinicId = useSelector(selectClinicId);
  const currentTherapistId = useSelector(selectTherapistId);
  const currentTherapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const englishCommunicationEnabled = useSelector(
    selectEnglishCommunicationEnabled
  );
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const isAdmin = useSelector(selectIsAdmin);
  // Multi-clinic v2: a non-admin with `calendar.manageOthersAppointments`
  // (e.g. a manager/secretary) may schedule and reassign on behalf of other
  // therapists. Legacy therapists resolve to false → today's UI exactly.
  const canManageOthersAppointments = useSelector(
    selectHasPermission("calendar.manageOthersAppointments")
  );
  const canScheduleForOthers = isAdmin || canManageOthersAppointments === true;
  // E6: patient ↔ therapist assignment gate. In clinic-wide sharing mode
  // every therapist may treat every patient, so the gate is off entirely.
  const clinicSharesAllPatients = useSelector(selectClinicSharesAllPatients);
  const canReassignPatients = useSelector(selectHasPermission("patients.reassign"));
  // Clinic scheduling policies (multi-clinic v2). Absent ⇒ warn-only.
  const preventTherapistDoubleBooking = useSelector(selectPreventTherapistDoubleBooking);
  const blockBookingOnAbsence = useSelector(selectBlockBookingOnAbsence);
  const canOverrideTherapistConflict = !preventTherapistDoubleBooking || isAdmin;
  const canOverrideAbsence = !blockBookingOnAbsence || isAdmin;
  const isEndMeetingSmsEnabled = useSelector(selectIsEndMeetingSmsEnabled);
  const tempCustomersEnabled = useSelector(selectTempCustomersEnabled);
  const punchCardsEnabled = useSelector(selectPunchCardsEnabled);
  const onlineMeetingsEnabled = useSelector(selectOnlineMeetingsEnabled);

  const { checkSMS, refreshUsageCache, PlanLimitModalComponent } =
    usePlanPermissions();

  const isAdminScheduling = !!targetTherapistId;
  // EDIT: anchor to the appointment's ORIGINAL therapist (same rule
  // ClinicCalendarModal already applies via targetTherapistId). Without this,
  // an admin editing a staff member's meeting from the admin calendar / diary
  // tab / room modal stamped their own therapistName on the doc (therapistId
  // stayed the owner's), ran the overlap + SMS-settings queries against the
  // wrong therapist, and attributed the fallback transaction to themselves.
  // (RoomCalendarModal passes firstActiveTherapist as target even for edits,
  // so the appointment's own therapist must win over targetTherapistId.)
  const baseTherapistId =
    (isEditing && appointment?.therapistId) ||
    targetTherapistId ||
    currentTherapistId;
  const baseTherapistName =
    (isEditing && appointment?.therapistId && appointment?.therapistName) ||
    targetTherapistName ||
    currentTherapistName;

  const defaultDuration = clinicProfile?.defaultMeetingTime || 45;
  const defaultRate = clinicProfile?.defaultHourlyRate || "";

  // ===== Mutations & queries =====
  const [createAppointment, { isLoading: isCreating }] =
    useCreateAppointmentMutation();
  const [createRecurringAppointments, { isLoading: isCreatingRecurring }] =
    useCreateRecurringAppointmentsMutation();
  const [updateAppointment, { isLoading: isUpdating }] =
    useUpdateAppointmentMutation();
  const [updateRecurringAppointments, { isLoading: isUpdatingRecurring }] =
    useUpdateRecurringAppointmentsMutation();
  const [deleteAppointment, { isLoading: isDeleting }] =
    useDeleteAppointmentMutation();
  const [deleteRecurringAppointments, { isLoading: isDeletingRecurring }] =
    useDeleteRecurringAppointmentsMutation();
  const [uncancelAppointment, { isLoading: isRestoring }] =
    useUncancelAppointmentMutation();
  const [createBlockedTime, { isLoading: isCreatingBlock }] =
    useCreateBlockedTimeMutation();
  const [updatePatientTherapistAssignments, { isLoading: isAssigningTherapist }] =
    useUpdatePatientTherapistAssignmentsMutation();
  const [createConfirmationLink] = useCreateConfirmationLinkMutation();
  const [deactivateConfirmationLink] = useDeactivateConfirmationLinkMutation();
  const [cancelScheduledSms] = useCancelScheduledSmsMutation();
  const [sendReminderNow] = useSendReminderNowMutation();

  const { data: confirmationLinks = [], refetch: refetchLinks } =
    useGetConfirmationLinksQuery(
      { clinicId, appointmentId: appointment?.id },
      { skip: !isEditing || !appointment?.id, refetchOnMountOrArgChange: true }
    );

  // Live copy of the edited appointment — the prop is a snapshot the parent
  // captured on click; status-driven UI (cancelled banner, restore) must read
  // the subscribed doc.
  const { data: liveFetchedAppointment } = useGetAppointmentQuery(
    { clinicId, appointmentId: appointment?.id },
    { skip: !clinicId || !appointment?.id }
  );
  const liveAppointment = liveFetchedAppointment || appointment;

  // Series siblings — scope-dialog hints ("N מפגשים נוספים יזוזו").
  const { data: seriesAppointments = [], refetch: refetchSeries } =
    useGetRecurringGroupAppointmentsQuery(
      {
        clinicId,
        recurringGroupId: appointment?.recurringGroupId,
        currentTherapistId,
        isAdmin,
      },
      {
        skip: !isEditing || !appointment?.recurringGroupId,
        refetchOnMountOrArgChange: true,
      }
    );

  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );
  const { data: clinicRooms = [] } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId }
  );
  const { data: therapistSmsSettings } = useGetSmsSettingsQuery(
    { therapistId: baseTherapistId },
    { skip: !baseTherapistId }
  );
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId || (!isAdminScheduling && !allowMultipleTherapists) }
  );

  // Multi-clinic v2 Phase 3: in "all" patient-sharing mode the patient picker
  // searches the whole clinic (same `therapistId: undefined` query the admin
  // pages use). Callers still pass their own (therapist-scoped) list, which
  // remains the source in every other clinic — zero change for legacy clinics.
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  const { data: sharedPatientsData } = useGetPatientsQuery(
    { clinicId, therapistId: undefined, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !sharesAllPatients }
  );
  const patients = useMemo(
    () =>
      sharesAllPatients && sharedPatientsData?.patients
        ? sharedPatientsData.patients
        : patientsProp || [],
    [sharesAllPatients, sharedPatientsData?.patients, patientsProp]
  );

  // The picker list comes from the caller's cached getPatients query (Home /
  // Calendar), which only refetches on local mutations. A patient created or
  // assigned to this therapist by another user (admin / secretary, other
  // device) is invisible here until the page remounts — while the patients
  // list page, which remounts on every visit, already shows them. Refresh the
  // list once per wizard open (create mode only; the patient is fixed on edit).
  useEffect(() => {
    if (isEditing || !clinicId) return;
    dispatch(
      patientsApi.util.invalidateTags([{ type: "Patients", id: "LIST" }])
    );
  }, [dispatch, isEditing, clinicId]);

  // ===== Form state =====
  const [formData, setFormData] = useState({
    patientId: "",
    patientName: "",
    isTempCustomer: false,
    tempCustomerPhone: "",
    date: "",
    startTime: "",
    duration: defaultDuration,
    notes: "",
    sendSms: true,
    sendEndMeetingSms: true,
    treatmentTypeId: "",
    paymentAmount: defaultRate ? defaultRate.toString() : "",
    isRecurring: false,
    recurringFrequency: RECURRING_FREQUENCY.WEEKLY,
    recurringSecondDay: "",
    recurringSecondTime: "",
    seriesTotalCount: 10,
    seriesStartIndex: 1,
    roomId: "",
    // Online video meeting (100ms)
    isOnlineMeeting: false,
    // Fallback: SMS with just the join link when the confirmation SMS is off
    sendVideoLinkSms: true,
    // Blocked time (חסימת זמן פרטי)
    isBlockedTime: false,
    blockCause: "",
    blockComments: "",
    blockRepeat: "none",
    blockUntilDate: "",
  });
  const [rateSource, setRateSource] = useState(defaultRate ? "clinic" : null);
  // Where the current duration came from: "clinic" | "patient" |
  // "treatmentType" | "lastAppointment" | "manual". Display-only hint.
  const [durationSource, setDurationSource] = useState("clinic");
  const [appointmentTherapistId, setAppointmentTherapistId] = useState("");
  const [seriesOverrides, setSeriesOverrides] = useState({});
  // Fields the therapist explicitly touched — smart defaults never overwrite them.
  const touchedRef = useRef(new Set());

  // Blocked-time entries use a two-step flow (type → block screen); edit mode
  // opens directly on the block screen, never on the meeting summary.
  const isBlockMode =
    formData.isBlockedTime || appointment?.isBlockedTime === true;

  // ===== Wizard step state ===== (edit opens summary-first)
  const [step, setStep] = useState(
    isEditing
      ? appointment?.isBlockedTime === true
        ? WIZARD_STEPS.SCHEDULE
        : WIZARD_STEPS.SUMMARY
      : WIZARD_STEPS.PATIENT
  );
  const [maxStepReached, setMaxStepReached] = useState(
    isEditing
      ? appointment?.isBlockedTime === true
        ? WIZARD_STEPS.SCHEDULE
        : WIZARD_STEPS.SUMMARY
      : WIZARD_STEPS.PATIENT
  );
  const [stepErrors, setStepErrors] = useState({});
  const [returnToSummary, setReturnToSummary] = useState(false);

  // ===== Confirmation gates (inline, auto-reset when inputs change) =====
  const [pastDateConfirmed, setPastDateConfirmed] = useState(false);
  const [overlapConfirmed, setOverlapConfirmed] = useState(false);
  // One-click waiver for a therapist absence covering the slot (policy-gated)
  const [absenceConfirmed, setAbsenceConfirmed] = useState(false);
  const [noRoomConfirmed, setNoRoomConfirmed] = useState(false);
  // Admin-only "book anyway" waiver for a blocked room (taken/closed/holiday)
  const [roomOverrideConfirmed, setRoomOverrideConfirmed] = useState(false);
  const [zeroAmountConfirmed, setZeroAmountConfirmed] = useState(false);

  // ===== Server-side series conflicts =====
  const [recurringConflicts, setRecurringConflicts] = useState([]);
  const allowRecurringConflictsRef = useRef(false);

  // ===== SMS preview (summary step) =====
  const [smsPreviewItems, setSmsPreviewItems] = useState([]);

  const [smsError, setSmsError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // ===== Edit-mode state =====
  const [initialFormData, setInitialFormData] = useState(null);
  const [editScope, setEditScope] = useState("single");
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const pendingEditDataRef = useRef(null);
  // Set when re-submitting a series edit through a waived conflict — skips
  // re-asking for the scope.
  const reuseEditScopeRef = useRef(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDialogWorking, setDeleteDialogWorking] = useState(false);
  const [deleteScope, setDeleteScope] = useState("single");
  const [showRecurringDeleteDialog, setShowRecurringDeleteDialog] =
    useState(false);
  const [recurringDeleteState, setRecurringDeleteState] = useState({
    linkedTransaction: undefined,
    preloadError: null,
    deleteLinkedTransactions: false,
    includePast: false,
    isWorking: false,
  });
  // Series-overview inline row editing
  const [editingOverviewRow, setEditingOverviewRow] = useState(null);
  const [overviewEditValues, setOverviewEditValues] = useState({});
  // Non-null while the scope dialog is answering for a series-overview row
  // instead of the main form.
  const [rowScopeEdit, setRowScopeEdit] = useState(null);
  const [pastReassignConfirm, setPastReassignConfirm] = useState({
    open: false,
    apt: null,
  });
  const [restoringRowId, setRestoringRowId] = useState(null);
  const [seriesCancelPrompt, setSeriesCancelPrompt] = useState({
    open: false,
    aptId: null,
    reason: "",
    isWorking: false,
  });
  // Communication (2c): live SMS state, queued-SMS cancel, late send, links
  const [liveSmsState, setLiveSmsState] = useState(null);
  const [cancelSmsTarget, setCancelSmsTarget] = useState(null);
  const [cancellingSmsType, setCancellingSmsType] = useState(null);
  const [showLateSmsDialog, setShowLateSmsDialog] = useState(false);
  const [lateSmsScope, setLateSmsScope] = useState("single");
  const [lateSmsIncludeReminder, setLateSmsIncludeReminder] = useState(false);
  const [lateSmsSending, setLateSmsSending] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  // Manual "send reminder now" (server-orchestrated, CK-3827)
  const [showReminderNowDialog, setShowReminderNowDialog] = useState(false);
  const [reminderNowSending, setReminderNowSending] = useState(false);

  // ===== Rooms =====
  const roomsEnabled = clinicRooms.some((room) => room.isActive !== false);
  const accessibleRooms = useMemo(() => {
    if (isAdminScheduling && !isEditing) {
      return clinicRooms.filter((room) => room.isActive !== false);
    }
    return clinicRooms.filter(
      (room) =>
        room.isActive !== false &&
        (room.allowOthersToBook === true ||
          room.defaultTherapistId === baseTherapistId)
    );
  }, [clinicRooms, baseTherapistId, isAdminScheduling, isEditing]);

  const defaultRoomForTherapist = useMemo(
    () =>
      clinicRooms.find(
        (room) =>
          room.isActive !== false && room.defaultTherapistId === baseTherapistId
      ),
    [clinicRooms, baseTherapistId]
  );

  // ===== Multi-therapist =====
  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === formData.patientId) || null,
    [patients, formData.patientId]
  );
  // Communication language for this patient's SMS and confirmation page.
  // Hebrew unless the clinic opted in AND this patient is flagged English.
  const smsLanguage = useMemo(
    () => getPatientLanguage(selectedPatient, englishCommunicationEnabled),
    [selectedPatient, englishCommunicationEnabled]
  );

  const selectedPatientTherapistIds = useMemo(
    () => (selectedPatient ? getPatientTherapistIds(selectedPatient) : []),
    [selectedPatient]
  );
  const isMultiTherapistPatient =
    allowMultipleTherapists && selectedPatientTherapistIds.length > 1;

  // Ownership-picker lists (multi-clinic v2): only active, non-secretary
  // therapists may be picked. `clinicTherapists` stays unfiltered for name
  // lookups of historical rows (inactive therapists keep their name).
  const assignableTherapists = useMemo(
    () => clinicTherapists.filter(isAssignableTherapist),
    [clinicTherapists]
  );
  // Therapist ids offered in the "מטפל/ת אחראי/ת" picker and the per-row
  // series override. Ids not yet resolvable (list still loading) are kept so
  // the picker never flashes empty; the currently selected id is always kept
  // so an existing appointment of a since-deactivated therapist still renders.
  const pickerTherapistIds = useMemo(() => {
    if (clinicTherapists.length === 0) return selectedPatientTherapistIds;
    return selectedPatientTherapistIds.filter((tid) => {
      if (tid === appointmentTherapistId) return true;
      const t = clinicTherapists.find((ct) => ct.uid === tid);
      return t ? isAssignableTherapist(t) : true;
    });
  }, [selectedPatientTherapistIds, clinicTherapists, appointmentTherapistId]);

  // Final therapistId/therapistName — multi-therapist override applies to
  // single (non-series) appointments only, exactly like AppointmentModal.
  const therapistId =
    appointmentTherapistId && !formData.isRecurring && isMultiTherapistPatient
      ? appointmentTherapistId
      : baseTherapistId;
  const therapistName = useMemo(() => {
    if (appointmentTherapistId && !formData.isRecurring && isMultiTherapistPatient) {
      const t = clinicTherapists.find((ct) => ct.uid === appointmentTherapistId);
      if (t) return `${t.firstName} ${t.lastName}`;
    }
    return baseTherapistName;
  }, [appointmentTherapistId, formData.isRecurring, isMultiTherapistPatient, clinicTherapists, baseTherapistName]);

  // ===== E6: patient ↔ therapist assignment gate =====
  // Booking a real patient for a therapist who is not assigned to them
  // (assigned sharing mode only; never on the booker's own calendar). The
  // ids assigned from inside the wizard are kept locally so the gate clears
  // immediately, before the patients refetch lands.
  const [assignedTherapistIds, setAssignedTherapistIds] = useState([]);
  useEffect(() => {
    setAssignedTherapistIds([]);
  }, [formData.patientId]);

  const effectivePatientTherapistIds = useMemo(
    () => [...new Set([...selectedPatientTherapistIds, ...assignedTherapistIds])],
    [selectedPatientTherapistIds, assignedTherapistIds]
  );
  const therapistDisplayName = useCallback(
    (tid) => {
      if (!tid) return "";
      if (tid === therapistId && therapistName) return therapistName;
      const t = clinicTherapists.find((ct) => ct.uid === tid);
      return t ? `${t.firstName || ""} ${t.lastName || ""}`.trim() : "";
    },
    [clinicTherapists, therapistId, therapistName]
  );
  // Create-only: an existing appointment keeps its therapist even if the
  // patient was reassigned since (editing must never be blocked by this).
  const unassignedCheckActive =
    !isEditing &&
    !clinicSharesAllPatients &&
    !!selectedPatient &&
    !formData.isTempCustomer &&
    !formData.isBlockedTime;
  const isTherapistUnassigned = useCallback(
    (tid) =>
      !!tid &&
      tid !== currentTherapistId &&
      !effectivePatientTherapistIds.includes(tid),
    [currentTherapistId, effectivePatientTherapistIds]
  );
  const unassignedTherapistForPatient = useMemo(() => {
    if (!unassignedCheckActive || !isTherapistUnassigned(therapistId)) return null;
    return { id: therapistId, name: therapistName || "המטפל/ת" };
  }, [unassignedCheckActive, isTherapistUnassigned, therapistId, therapistName]);
  // Series occurrences whose per-row therapist override is not assigned to
  // the patient (skipped rows are ignored).
  const seriesUnassignedIdx = useMemo(() => {
    if (!unassignedCheckActive || !formData.isRecurring) return [];
    return Object.keys(seriesOverrides)
      .map(Number)
      .filter((idx) => {
        const ov = seriesOverrides[idx];
        return ov && ov.skip !== true && isTherapistUnassigned(ov.therapistId);
      })
      .sort((a, b) => a - b);
  }, [unassignedCheckActive, formData.isRecurring, seriesOverrides, isTherapistUnassigned]);
  const hasUnassignedTherapist =
    !!unassignedTherapistForPatient || seriesUnassignedIdx.length > 0;
  // Admins (and `calendar.manageOthersAppointments` holders) may book without
  // assigning — a one-click "קבע/י בכל זאת" acknowledgement, kept locally and
  // reset whenever the patient or the target therapist changes. Everyone else
  // stays blocked until the patient is assigned.
  const canAcknowledgeUnassigned = canScheduleForOthers;
  const [unassignedAcknowledged, setUnassignedAcknowledged] = useState(false);
  useEffect(() => {
    setUnassignedAcknowledged(false);
  }, [formData.patientId, therapistId]);
  const isPatientUnassignedBlocked =
    hasUnassignedTherapist && !(canAcknowledgeUnassigned && unassignedAcknowledged);
  // Who may fix it inline: multi-therapist clinics only; the Cloud Function
  // itself requires admin or `patients.reassign`, so mirror that here.
  const canAssignTherapistToPatient =
    allowMultipleTherapists && (isAdmin || canReassignPatients === true);

  const assignTherapistToPatient = useCallback(
    async (targetTherapistIdArg) => {
      const tid = targetTherapistIdArg || unassignedTherapistForPatient?.id;
      if (!tid || !selectedPatient?.id || !clinicId || !canAssignTherapistToPatient) {
        return false;
      }
      const name = therapistDisplayName(tid) || "המטפל/ת";
      const nextIds = [...new Set([...effectivePatientTherapistIds, tid])];
      try {
        await updatePatientTherapistAssignments({
          clinicId,
          patientId: selectedPatient.id,
          patientName: formData.patientName,
          therapistIds: nextIds,
          therapistId: currentTherapistId,
          therapistName: currentTherapistName,
        }).unwrap();
        setAssignedTherapistIds((prev) => (prev.includes(tid) ? prev : [...prev, tid]));
        showSuccess(`${name} שויך/ה למטופל/ת בהצלחה`);
        return true;
      } catch (error) {
        const msg = error?.message || "";
        const denied = /permission-denied|Only clinic admins/i.test(msg);
        console.error("[CK-3850] Error assigning therapist to patient from wizard:", {
          errorCode: "CK-3850",
          errorMessage: msg,
          clinicId,
          patientId: selectedPatient.id,
          targetTherapistId: tid,
          timestamp: new Date().toISOString(),
        });
        showError(
          denied
            ? "אין לך הרשאה לשייך מטפל/ת למטופל/ת. פנה/י למנהל/ת הקליניקה."
            : "שיוך המטפל/ת למטופל/ת נכשל, נסה/י שוב"
        );
        return false;
      }
    },
    [
      unassignedTherapistForPatient,
      selectedPatient,
      clinicId,
      canAssignTherapistToPatient,
      therapistDisplayName,
      effectivePatientTherapistIds,
      updatePatientTherapistAssignments,
      formData.patientName,
      currentTherapistId,
      currentTherapistName,
      showSuccess,
      showError,
    ]
  );

  // ===== Punch card =====
  const [usePunchCard, setUsePunchCard] = useState(false);
  const prevRateBeforePunchRef = useRef(null);
  const { data: activePunchCard = null } = useGetActivePunchCardQuery(
    { clinicId, patientId: formData.patientId },
    {
      skip:
        !punchCardsEnabled ||
        !canAccessFinance ||
        !clinicId ||
        !formData.patientId,
    }
  );
  const remainingPunches = activePunchCard
    ? Math.max(
        0,
        (activePunchCard.totalVisits || 0) - (activePunchCard.usedVisits || 0)
      )
    : 0;
  const punchAmountForCard = activePunchCard
    ? activePunchCard.paidUpfront === true &&
      activePunchCard.purchaseIncomeRecorded === true
      ? 0
      : activePunchCard.pricePerVisit || 0
    : 0;
  // Create: only when the card has remaining punches. Edit: single,
  // non-cancelled appointments — either to un-punch an already punched meeting
  // or to punch one with remaining visits (mirrors AppointmentModal).
  const showPunchOption =
    punchCardsEnabled &&
    canAccessFinance &&
    !!formData.patientId &&
    (!isEditing
      ? remainingPunches > 0
      : !appointment?.isRecurring &&
        appointment?.status !== "cancelled" &&
        (appointment?.isPunchCard === true || remainingPunches > 0));
  const rateDisabled = usePunchCard && (isEditing || !formData.isRecurring);

  // Missing-rate gate — a meeting created without an amount never gets a
  // financial transaction, so it silently vanishes from collection/reports.
  // Create-only, like the classic modal.
  const needsAmountGate =
    !isEditing &&
    canAccessFinance &&
    !rateDisabled &&
    !(parseFloat(formData.paymentAmount) > 0);

  // ===== Live checks =====
  // 30s tick keeps the past-time check honest while the modal stays open
  // (a slot can slip into the past without any user input).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const isPastDateTime = useMemo(
    () => isPastDateTimeValue(formData.date, formData.startTime, new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.date, formData.startTime, nowTick]
  );
  const {
    overlappingAppointment,
    therapistConflicts,
    therapistCheckPending,
    therapistCheckFailed,
    therapistAbsence,
    absenceCheckFailed,
    overlappingBlock,
    roomConflicts,
    roomAvailabilityError,
    roomAvailabilityReason,
    roomCheckPending,
  } = useAppointmentChecks({
    clinicId,
    therapistId,
    date: formData.date,
    startTime: formData.startTime,
    duration: formData.duration,
    roomId: formData.roomId,
    roomsEnabled,
    excludeAppointmentId: appointment?.id || null,
  });

  // ===== Series preview (mirrors AppointmentModal's seriesPreview memo) =====
  const seriesPreview = useMemo(() => {
    if (!formData.isRecurring || !formData.date || isEditing) {
      return { dates: [], count: 0, items: [] };
    }
    const totalCount = parseInt(formData.seriesTotalCount, 10) || 0;
    const startIndex = parseInt(formData.seriesStartIndex, 10) || 1;
    if (totalCount <= 0 || startIndex < 1 || startIndex > totalCount) {
      return { dates: [], count: 0, items: [] };
    }
    const startDate = new Date(`${formData.date}T${formData.startTime || "09:00"}`);
    if (isNaN(startDate.getTime())) {
      return { dates: [], count: 0, items: [] };
    }
    const isTwiceWeekly =
      formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY;
    const secondSlot =
      isTwiceWeekly &&
      formData.recurringSecondDay !== "" &&
      formData.recurringSecondTime
        ? {
            dayOfWeek: Number(formData.recurringSecondDay),
            time: formData.recurringSecondTime,
          }
        : null;
    if (isTwiceWeekly && !secondSlot) {
      return { dates: [], count: 0, items: [] };
    }

    const baseOccurrences = calculateSeriesOccurrences(
      startDate,
      formData.recurringFrequency,
      totalCount,
      startIndex,
      secondSlot
    );
    const baseTime = formData.startTime || "09:00";

    const items = baseOccurrences.map(({ date, slot }, index) => {
      const override = seriesOverrides[index];
      let finalDate = new Date(date);
      let finalTime =
        slot === SERIES_SLOT.SECONDARY ? formData.recurringSecondTime : baseTime;

      if (override?.date) {
        const [y, m, d] = override.date.split("-").map(Number);
        finalDate.setFullYear(y, m - 1, d);
      }
      if (override?.time) {
        finalTime = override.time;
        const [h, min] = override.time.split(":").map(Number);
        finalDate.setHours(h, min, 0, 0);
      }

      return {
        date: finalDate,
        time: finalTime,
        dateStr: override?.date || formatLocalDateString(date),
        seriesIndex: startIndex + index,
        slot,
        isOverridden: !!override,
      };
    });

    return {
      dates: items.map((i) => i.date),
      items,
      count: baseOccurrences.length,
      totalCount,
      startIndex,
    };
  }, [
    formData.isRecurring,
    formData.date,
    formData.startTime,
    formData.seriesTotalCount,
    formData.seriesStartIndex,
    formData.recurringFrequency,
    formData.recurringSecondDay,
    formData.recurringSecondTime,
    seriesOverrides,
    isEditing,
  ]);

  // ===== Hydrate from the edited appointment (mirrors the edit-init effect) =====
  useEffect(() => {
    if (!appointment) return;
    const startDate = new Date(appointment.startTime);
    setFormData((prev) => ({
      ...prev,
      patientId: appointment.patientId || "",
      patientName: appointment.patientName || "",
      isTempCustomer: appointment.isTempCustomer === true,
      tempCustomerPhone: appointment.tempCustomerPhone || "",
      date: formatLocalDateString(startDate),
      startTime: startDate.toTimeString().slice(0, 5),
      duration: appointment.duration || defaultDuration,
      notes: appointment.notes || "",
      sendSms: false,
      // Keep the stored opt-in/out: the edit payload writes sendEndMeetingSms
      // back and the toggle is hidden in edit mode, so the default `true`
      // silently re-enabled the post-meeting SMS on every edit.
      sendEndMeetingSms: appointment.sendEndMeetingSms === true,
      treatmentTypeId: appointment.treatmentTypeId || "",
      paymentAmount: appointment.paymentAmount?.toString() || "",
      isRecurring: appointment.isRecurring || false,
      recurringFrequency:
        appointment.recurringFrequency || RECURRING_FREQUENCY.WEEKLY,
      roomId: appointment.roomId || "",
      isOnlineMeeting: appointment.isOnlineMeeting === true,
      // Blocked time — repeat settings are fixed after creation (like series
      // frequency); only cause/comments/date/time/duration are editable.
      isBlockedTime: appointment.isBlockedTime === true,
      blockCause: appointment.blockCause || "",
      blockComments: appointment.blockComments || "",
      blockRepeat: "none",
      blockUntilDate: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.id]);

  // Editing an already punched appointment opens with the checkbox on.
  useEffect(() => {
    if (isEditing) {
      const punched = appointment?.isPunchCard === true;
      setUsePunchCard(punched);
      if (punched) setRateSource("punchCard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, appointment?.id, appointment?.isPunchCard]);

  // Dirty-compare baseline — snapshot after the hydrate/prefill effects settle.
  useEffect(() => {
    if (initialFormData === null) {
      const timeoutId = setTimeout(() => {
        setInitialFormData({ ...formData });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [formData, initialFormData]);

  // ===== Series room availability (per occurrence) =====
  // The single-slot room check above only covers the base occurrence. For a
  // series, every occurrence with an effective room (base room or per-meeting
  // override) is checked against the room's calendar — debounced batch.
  const [seriesRoomConflicts, setSeriesRoomConflicts] = useState([]);
  const [seriesRoomCheckPending, setSeriesRoomCheckPending] = useState(false);
  const seriesRoomDebounceRef = useRef(null);
  const excludeSeriesAppointmentId = appointment?.id || null;

  // Effective room per occurrence — undefined override = base room;
  // "" / null override = explicitly no room.
  const seriesRoomCheckKey = useMemo(() => {
    if (isEditing || !formData.isRecurring || !roomsEnabled) return "";
    return JSON.stringify(
      seriesPreview.items.map((item, idx) => {
        const ov = seriesOverrides[idx];
        if (ov?.skip === true) return null; // skipped occurrence — nothing to check
        const roomId =
          ov?.roomId !== undefined ? ov.roomId || null : formData.roomId || null;
        return roomId ? [item.dateStr, item.time, roomId] : null;
      })
    );
  }, [isEditing, formData.isRecurring, formData.roomId, roomsEnabled, seriesPreview.items, seriesOverrides]);

  useEffect(() => {
    setSeriesRoomConflicts([]);
    if (!seriesRoomCheckKey || !clinicId) {
      setSeriesRoomCheckPending(false);
      return undefined;
    }
    const slots = JSON.parse(seriesRoomCheckKey);
    if (!slots.some(Boolean)) {
      setSeriesRoomCheckPending(false);
      return undefined;
    }

    let cancelled = false;
    setSeriesRoomCheckPending(true);
    if (seriesRoomDebounceRef.current) clearTimeout(seriesRoomDebounceRef.current);
    seriesRoomDebounceRef.current = setTimeout(async () => {
      try {
        const duration = parseInt(formData.duration, 10) || 45;
        const results = await Promise.all(
          slots.map(async (slot, idx) => {
            if (!slot) return null;
            const [dateStr, time, roomId] = slot;
            const start = new Date(`${dateStr}T${time}`);
            if (isNaN(start.getTime())) return null;
            const end = new Date(start.getTime() + duration * 60000);
            try {
              const hours = await resolveRoomAvailability(
                clinicId,
                roomId,
                start,
                end
              );
              if (!hours.available) {
                return {
                  idx,
                  dateStr,
                  time,
                  roomId,
                  cause: hours.reason, // 'closed_day' | 'outside_hours' | 'holiday'
                  message: hours.message,
                };
              }
              const availability = await checkRoomAvailabilityFn(
                clinicId,
                roomId,
                start,
                end,
                excludeSeriesAppointmentId
              );
              if (!availability.isAvailable) {
                return {
                  idx,
                  dateStr,
                  time,
                  roomId,
                  cause: "taken",
                  message: "החדר תפוס בזמן הזה",
                  conflictingPatientName:
                    availability.conflicts?.[0]?.patientName || null,
                  conflictingTherapistId:
                    availability.conflicts?.[0]?.therapistId || null,
                };
              }
              return null;
            } catch (slotError) {
              console.error("[CK-3817] Series room availability check failed:", {
                errorCode: "CK-3817",
                errorMessage: slotError?.message,
                clinicId,
                roomId,
                timestamp: new Date().toISOString(),
              });
              return null;
            }
          })
        );
        if (cancelled) return;
        setSeriesRoomConflicts(results.filter(Boolean));
      } finally {
        if (!cancelled) setSeriesRoomCheckPending(false);
      }
    }, 800);

    return () => {
      cancelled = true;
      if (seriesRoomDebounceRef.current) clearTimeout(seriesRoomDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesRoomCheckKey, clinicId, formData.duration]);

  // ===== Series edit: room warnings for future occurrences (non-blocking) =====
  // When editing a series meeting and the room or start hour changed, check the
  // future sibling occurrences against the new room. Warn-only — never blocks —
  // so existing production edit flows keep working.
  const [seriesEditRoomWarnings, setSeriesEditRoomWarnings] = useState([]);
  const seriesEditRoomDebounceRef = useRef(null);
  useEffect(() => {
    setSeriesEditRoomWarnings([]);
    if (
      !isEditing ||
      !roomsEnabled ||
      !clinicId ||
      !formData.roomId ||
      !appointment?.recurringGroupId
    ) {
      return undefined;
    }
    const roomChanged = (formData.roomId || "") !== (appointment.roomId || "");
    const originalTime = appointment.startTime
      ? new Date(appointment.startTime).toTimeString().slice(0, 5)
      : null;
    const timeChanged =
      !!formData.startTime && !!originalTime && formData.startTime !== originalTime;
    if (!roomChanged && !timeChanged) return undefined;

    const now = new Date();
    const futureSiblings = seriesAppointments
      .filter(
        (a) =>
          a.id !== appointment.id &&
          a.isDeleted !== true &&
          a.status !== "cancelled" &&
          a.status !== "completed" &&
          a.startTime &&
          new Date(a.startTime) > now
      )
      .slice(0, 20);
    if (futureSiblings.length === 0) return undefined;

    let cancelled = false;
    if (seriesEditRoomDebounceRef.current) clearTimeout(seriesEditRoomDebounceRef.current);
    seriesEditRoomDebounceRef.current = setTimeout(async () => {
      try {
        const duration = parseInt(formData.duration, 10) || 45;
        const results = await Promise.all(
          futureSiblings.map(async (sibling) => {
            try {
              const start = new Date(sibling.startTime);
              if (timeChanged) {
                const [h, m] = formData.startTime.split(":").map(Number);
                start.setHours(h, m, 0, 0);
              }
              const end = new Date(start.getTime() + duration * 60000);
              const hours = await resolveRoomAvailability(clinicId, formData.roomId, start, end);
              if (!hours.available) {
                return { appointmentId: sibling.id, startTime: start.toISOString(), cause: hours.reason, message: hours.message };
              }
              const availability = await checkRoomAvailabilityFn(clinicId, formData.roomId, start, end, sibling.id);
              if (!availability.isAvailable) {
                return { appointmentId: sibling.id, startTime: start.toISOString(), cause: "taken", message: "החדר תפוס בזמן הזה" };
              }
              return null;
            } catch (err) {
              console.error("[CK-3818] Series edit room warning check failed:", {
                errorCode: "CK-3818",
                errorMessage: err?.message,
                clinicId,
                roomId: formData.roomId,
                timestamp: new Date().toISOString(),
              });
              return null;
            }
          })
        );
        if (!cancelled) setSeriesEditRoomWarnings(results.filter(Boolean));
      } catch (err) {
        // per-sibling errors already logged above
      }
    }, 800);
    return () => {
      cancelled = true;
      if (seriesEditRoomDebounceRef.current) clearTimeout(seriesEditRoomDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, roomsEnabled, clinicId, formData.roomId, formData.startTime, formData.duration, appointment?.id, appointment?.roomId, appointment?.startTime, appointment?.recurringGroupId, seriesAppointments]);

  // ===== Prefill (create) — mirrors AppointmentModal's prefill effect =====
  useEffect(() => {
    if (isEditing) return;
    if (prefilledDate) {
      if (prefilledDateOnly) {
        setFormData((prev) => ({
          ...prev,
          date: formatLocalDateString(prefilledDate),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          date: formatLocalDateString(prefilledDate),
          startTime: prefilledDate.toTimeString().slice(0, 5),
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        date: formatLocalDateString(new Date()),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default room: caller pre-selection wins, then the therapist's own room.
  useEffect(() => {
    if (isEditing) return;
    if (!formData.roomId && !touchedRef.current.has("roomId")) {
      if (preSelectedRoomId) {
        setFormData((prev) => ({ ...prev, roomId: preSelectedRoomId }));
      } else if (defaultRoomForTherapist) {
        setFormData((prev) => ({ ...prev, roomId: defaultRoomForTherapist.id }));
      }
    }
  }, [defaultRoomForTherapist, formData.roomId, preSelectedRoomId, isEditing]);

  // SMS defaults — past meetings never send; otherwise follow therapist settings.
  // Create-only: edit mode never auto-sends (sendSms stays false from hydrate).
  useEffect(() => {
    if (isEditing) return;
    if (isPastDateTime) {
      setFormData((prev) => ({
        ...prev,
        sendSms: false,
        sendEndMeetingSms: false,
      }));
    } else {
      const smsDefault =
        therapistSmsSettings?.sendOnCreate !== false ||
        therapistSmsSettings?.sendReminderEnabled !== false;
      setFormData((prev) => ({
        ...prev,
        sendSms: smsDefault,
        sendEndMeetingSms: isEndMeetingSmsEnabled,
      }));
    }
  }, [
    isPastDateTime,
    isEditing,
    isEndMeetingSmsEnabled,
    therapistSmsSettings?.sendOnCreate,
    therapistSmsSettings?.sendReminderEnabled,
  ]);

  // ===== Gate auto-resets =====
  useEffect(() => {
    setPastDateConfirmed(false);
    setOverlapConfirmed(false);
    setAbsenceConfirmed(false);
  }, [formData.date, formData.startTime, formData.duration]);

  // Switching the target therapist invalidates both scheduling waivers.
  useEffect(() => {
    setOverlapConfirmed(false);
    setAbsenceConfirmed(false);
  }, [therapistId]);

  useEffect(() => {
    if (formData.roomId) setNoRoomConfirmed(false);
  }, [formData.roomId]);

  useEffect(() => {
    setRoomOverrideConfirmed(false);
  }, [formData.roomId, formData.date, formData.startTime, formData.duration]);

  useEffect(() => {
    setZeroAmountConfirmed(false);
  }, [formData.paymentAmount, usePunchCard]);

  // Any schedule change invalidates a server conflict result + waiver
  // (and, in edit, a pending scope-dialog payload).
  useEffect(() => {
    setRecurringConflicts((prev) => (prev.length === 0 ? prev : []));
    allowRecurringConflictsRef.current = false;
    reuseEditScopeRef.current = false;
    pendingEditDataRef.current = null;
  }, [
    formData.date,
    formData.startTime,
    formData.duration,
    formData.recurringFrequency,
    formData.seriesTotalCount,
    formData.seriesStartIndex,
    formData.recurringSecondDay,
    formData.recurringSecondTime,
    seriesOverrides,
  ]);

  // Punch checkbox resets when switching patient (card is per-patient).
  const punchPatientRef = useRef(formData.patientId);
  useEffect(() => {
    if (punchPatientRef.current !== formData.patientId) {
      punchPatientRef.current = formData.patientId;
      if (!isEditing && usePunchCard) {
        setUsePunchCard(false);
        prevRateBeforePunchRef.current = null;
      }
    }
  }, [formData.patientId, usePunchCard, isEditing]);

  // Toggling series mode while punched: series keeps the regular rate (it
  // applies to the uncovered occurrences), single shows the card's rate.
  useEffect(() => {
    if (isEditing || !usePunchCard || !activePunchCard) return;
    if (formData.isRecurring) {
      if (prevRateBeforePunchRef.current) {
        const prev = prevRateBeforePunchRef.current;
        prevRateBeforePunchRef.current = null;
        setFormData((p) => ({ ...p, paymentAmount: prev.amount }));
        setRateSource(prev.source);
      }
    } else if (rateSource !== "punchCard") {
      prevRateBeforePunchRef.current = {
        amount: formData.paymentAmount,
        source: rateSource,
      };
      setFormData((p) => ({
        ...p,
        paymentAmount: punchAmountForCard.toString(),
      }));
      setRateSource("punchCard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isRecurring]);

  // ===== Smart default: last appointment of the selected patient =====
  const { data: lastPatientAppointment } = useGetLastPatientAppointmentQuery(
    { clinicId, patientId: formData.patientId },
    { skip: isEditing || !clinicId || !formData.patientId || formData.isTempCustomer }
  );

  useEffect(() => {
    if (!lastPatientAppointment || !formData.patientId) return;
    if (lastPatientAppointment.patientId !== formData.patientId) return;

    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      // Treatment type: patient default wins; last appointment fills the gap.
      if (
        !prev.treatmentTypeId &&
        !touchedRef.current.has("treatmentTypeId") &&
        lastPatientAppointment.treatmentTypeId &&
        treatmentTypes.some((t) => t.id === lastPatientAppointment.treatmentTypeId)
      ) {
        next.treatmentTypeId = lastPatientAppointment.treatmentTypeId;
        changed = true;
        // Rate follows only when the patient has no personal rate and the
        // therapist hasn't touched the amount.
        if (
          !selectedPatient?.treatmentRate &&
          !touchedRef.current.has("paymentAmount")
        ) {
          const tt = treatmentTypes.find(
            (t) => t.id === lastPatientAppointment.treatmentTypeId
          );
          if (tt?.rate != null) {
            next.paymentAmount = tt.rate.toString();
            setRateSource("treatmentType");
          }
        }
        // Duration follows the type (patient default still wins, and a
        // touched duration is never overwritten).
        if (
          !touchedRef.current.has("duration") &&
          !selectedPatient?.treatmentDuration &&
          prev.duration === defaultDuration
        ) {
          const tt = treatmentTypes.find(
            (t) => t.id === lastPatientAppointment.treatmentTypeId
          );
          if (tt?.duration > 0 && tt.duration !== prev.duration) {
            next.duration = tt.duration;
            setDurationSource("treatmentType");
          }
        }
      }
      // Duration: patient default wins; last appointment fills the gap.
      if (
        !touchedRef.current.has("duration") &&
        !selectedPatient?.treatmentDuration &&
        lastPatientAppointment.duration &&
        next.duration === defaultDuration &&
        lastPatientAppointment.duration !== next.duration
      ) {
        next.duration = lastPatientAppointment.duration;
        setDurationSource("lastAppointment");
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [lastPatientAppointment, formData.patientId, treatmentTypes, selectedPatient, defaultDuration]);

  // ===== Field updates =====
  const updateField = useCallback(
    (name, value) => {
      touchedRef.current.add(name);

      if (name === "treatmentTypeId") {
        const selectedType = treatmentTypes.find((t) => t.id === value);
        const patientHasRate = selectedPatient?.treatmentRate;
        // Duration follows the type on CREATE only — on edit a silent endTime
        // move would reschedule the completion task + SMS timings. Patient
        // duration and a manually touched duration always win.
        const typeDuration = Number(selectedType?.duration);
        const applyDuration =
          !isEditing &&
          !selectedPatient?.treatmentDuration &&
          !touchedRef.current.has("duration") &&
          Number.isFinite(typeDuration) &&
          typeDuration > 0;
        const durationPatch = applyDuration ? { duration: typeDuration } : {};
        if (!patientHasRate && !rateDisabled) {
          const { amount, source } = resolveRate(null, selectedType, clinicProfile);
          setFormData((prev) => ({
            ...prev,
            treatmentTypeId: value,
            paymentAmount: amount,
            ...durationPatch,
          }));
          setRateSource(source);
        } else {
          setFormData((prev) => ({ ...prev, treatmentTypeId: value, ...durationPatch }));
        }
        if (applyDuration) {
          setDurationSource("treatmentType");
          setSeriesOverrides({});
        }
        return;
      }

      if (name === "paymentAmount") {
        setFormData((prev) => ({ ...prev, paymentAmount: value }));
        setRateSource("manual");
        return;
      }

      if (name === "duration") setDurationSource("manual");

      setFormData((prev) => ({ ...prev, [name]: value }));

      if (
        [
          "date",
          "startTime",
          "duration",
          "recurringFrequency",
          "seriesTotalCount",
          "seriesStartIndex",
          "recurringSecondDay",
          "recurringSecondTime",
        ].includes(name)
      ) {
        setSeriesOverrides({});
      }
    },
    [treatmentTypes, selectedPatient, clinicProfile, rateDisabled, isEditing]
  );

  // ===== Patient selection (mirrors handlePatientChange) =====
  // Picking a real patient auto-advances to the schedule step (post-commit
  // effect below, so downstream closures see the fresh formData).
  const autoAdvanceRef = useRef(false);
  const selectPatient = useCallback(
    (patientId) => {
      if (patientId === BLOCK_TIME_OPTION) {
        setFormData((prev) => ({
          ...prev,
          patientId: "",
          patientName: "זמן חסום",
          isTempCustomer: false,
          tempCustomerPhone: "",
          isBlockedTime: true,
          duration: 60,
          paymentAmount: "",
          isRecurring: false,
          roomId: "",
          treatmentTypeId: "",
        }));
        setRateSource(null);
        // Nothing else to fill on step 1 — jump straight to the block screen.
        autoAdvanceRef.current = true;
        return;
      }

      if (patientId === TEMP_CUSTOMER_OPTION) {
        setFormData((prev) => ({
          ...prev,
          patientId: "",
          patientName: "",
          isTempCustomer: true,
          tempCustomerPhone: "",
          isBlockedTime: false,
          duration: defaultDuration,
          paymentAmount: defaultRate ? defaultRate.toString() : "",
        }));
        setRateSource(defaultRate ? "clinic" : null);
        setDurationSource("clinic");
        return;
      }

      const patient = patients.find((p) => p.id === patientId);

      let newRate = defaultRate ? defaultRate.toString() : "";
      let newRateSource = defaultRate ? "clinic" : null;
      let newTreatmentTypeId = "";

      if (patient) {
        if (patient.defaultTreatmentTypeId) {
          const patientDefaultType = treatmentTypes.find(
            (t) => t.id === patient.defaultTreatmentTypeId
          );
          if (patientDefaultType) {
            newTreatmentTypeId = patientDefaultType.id;
            if (patientDefaultType.rate != null) {
              newRate = patientDefaultType.rate.toString();
              newRateSource = "treatmentType";
            }
          }
        }
        if (patient.treatmentRate) {
          newRate = patient.treatmentRate.toString();
          newRateSource = "patient";
        }
      }

      // Duration chain: patient.treatmentDuration → type.duration → clinic.
      // Mirrors the rate chain; identical to the old behavior when no type
      // carries a duration.
      const effectiveTypeId = newTreatmentTypeId || formData.treatmentTypeId;
      const effectiveType = treatmentTypes.find((t) => t.id === effectiveTypeId);
      const { duration: newDuration, source: newDurationSource } = resolveDuration(
        patient,
        effectiveType,
        clinicProfile
      );

      setFormData((prev) => ({
        ...prev,
        patientId,
        patientName: patient ? `${patient.firstName} ${patient.lastName}` : "",
        isTempCustomer: false,
        tempCustomerPhone: "",
        isBlockedTime: false,
        duration: newDuration,
        paymentAmount: newRate,
        treatmentTypeId: newTreatmentTypeId || prev.treatmentTypeId,
      }));
      setRateSource(newRateSource);
      setDurationSource(newDurationSource);

      // Multi-therapist: default the appointment therapist to the scheduler.
      if (allowMultipleTherapists && patient) {
        const pTids = getPatientTherapistIds(patient);
        if (pTids.length > 1) {
          setAppointmentTherapistId(
            pTids.includes(baseTherapistId) ? baseTherapistId : pTids[0]
          );
        } else {
          setAppointmentTherapistId("");
        }
      }

      if (patient) autoAdvanceRef.current = true;
    },
    [patients, treatmentTypes, defaultDuration, defaultRate, allowMultipleTherapists, baseTherapistId, clinicProfile, formData.treatmentTypeId]
  );

  // ===== Punch toggle (mirrors handlePunchToggle, create-mode branches) =====
  const handlePunchToggle = useCallback(
    (checked) => {
      setUsePunchCard(checked);
      if (formData.isRecurring && !isEditing) return;
      if (checked && (activePunchCard || appointment?.isPunchCard)) {
        prevRateBeforePunchRef.current = {
          amount: formData.paymentAmount,
          source: rateSource,
        };
        if (activePunchCard) {
          setFormData((prev) => ({
            ...prev,
            paymentAmount: punchAmountForCard.toString(),
          }));
        }
        setRateSource("punchCard");
      } else if (!checked) {
        if (prevRateBeforePunchRef.current) {
          const prev = prevRateBeforePunchRef.current;
          prevRateBeforePunchRef.current = null;
          setFormData((p) => ({ ...p, paymentAmount: prev.amount }));
          setRateSource(prev.source);
        } else {
          const { amount, source } = resolveRate(
            selectedPatient,
            treatmentTypes.find((t) => t.id === formData.treatmentTypeId),
            clinicProfile
          );
          setFormData((p) => ({ ...p, paymentAmount: amount }));
          setRateSource(source);
        }
      }
    },
    [formData.isRecurring, formData.paymentAmount, formData.treatmentTypeId, activePunchCard, punchAmountForCard, rateSource, selectedPatient, treatmentTypes, clinicProfile, isEditing, appointment?.isPunchCard]
  );

  // ===== Per-step validation =====
  const hasBlockingConflicts =
    recurringConflicts.length > 0 &&
    (recurringConflicts.some((c) => c.selfConflict) ||
      !allowRecurringConflictsRef.current);

  const validateStepFields = useCallback(
    (targetStep) => {
      switch (targetStep) {
        case WIZARD_STEPS.PATIENT:
          return validatePatient(formData);
        case WIZARD_STEPS.SCHEDULE:
          return validateSchedule(formData, { isEditing });
        case WIZARD_STEPS.DETAILS:
          return {
            ...validateDetails(formData),
            ...validateExternalConflicts({
              hasBlockingConflicts: false,
              roomsEnabled,
              roomId: formData.roomId,
              roomConflicts: isAdmin && roomOverrideConfirmed ? [] : roomConflicts,
              roomAvailabilityError:
                isAdmin && roomOverrideConfirmed ? null : roomAvailabilityError,
            }),
          };
        default:
          return {};
      }
    },
    [formData, roomsEnabled, roomConflicts, roomAvailabilityError, isEditing, isAdmin, roomOverrideConfirmed]
  );

  // Gates that must be confirmed before leaving each step. The past-date /
  // overlap / no-room / zero-amount confirmations are create-only, exactly
  // like the classic modal; blocking series conflicts always gate.
  // Therapist double-booking + absence gate (multi-clinic v2): applies to
  // create AND edit, to the therapist's own calendar and to others'. Policy
  // off ⇒ one-click waiver by anyone; policy on ⇒ admin-only waiver. The gate
  // also holds while the debounced overlap check is in flight.
  //
  // EDIT: the gate only applies when the slot actually changed (date, start
  // time, duration or therapist). Editing notes/payment of an appointment
  // that already overlaps must not be blocked by a conflict the user did not
  // create — the panel then renders as information only.
  const originalSlot = useMemo(() => {
    if (!isEditing || !appointment?.startTime) return null;
    const start = new Date(appointment.startTime);
    if (Number.isNaN(start.getTime())) return null;
    const end = appointment.endTime ? new Date(appointment.endTime) : null;
    const endDuration =
      end && !Number.isNaN(end.getTime())
        ? Math.round((end.getTime() - start.getTime()) / 60000)
        : null;
    return {
      date: formatLocalDateString(start),
      startTime: start.toTimeString().slice(0, 5),
      duration: Number(appointment.duration || endDuration || defaultDuration),
    };
  }, [isEditing, appointment?.startTime, appointment?.endTime, appointment?.duration, defaultDuration]);
  const slotChanged =
    !isEditing ||
    !originalSlot ||
    formData.date !== originalSlot.date ||
    formData.startTime !== originalSlot.startTime ||
    Number(formData.duration) !== originalSlot.duration ||
    therapistId !== appointment?.therapistId;
  // Edit with an untouched slot: conflicts/absences are shown, never gated.
  const therapistGateInformational = isEditing && !slotChanged;
  const therapistConflictBlocked =
    slotChanged &&
    !!overlappingAppointment &&
    !(overlapConfirmed && canOverrideTherapistConflict);
  const absenceBlocked =
    slotChanged && !!therapistAbsence && !(absenceConfirmed && canOverrideAbsence);
  const scheduleGateBlocked =
    (!isEditing && isPastDateTime && !pastDateConfirmed) ||
    therapistConflictBlocked ||
    absenceBlocked ||
    (slotChanged && therapistCheckPending) ||
    hasBlockingConflicts;
  // Room conflicts hard-block (create AND edit) — including while the
  // debounced availability checks are still in flight, so a fast "המשך"
  // click can't slip past a conflict that hasn't landed yet. Covers the base
  // slot and every series occurrence (incl. per-meeting room overrides).
  // Admin can waive a blocked room ("קבע בכל זאת") — per base slot via
  // roomOverrideConfirmed, per series occurrence via seriesOverrides[idx].override.
  const baseRoomIssue =
    !!formData.roomId &&
    (roomConflicts.length > 0 || !!roomAvailabilityError);
  const unresolvedSeriesRoomConflicts = seriesRoomConflicts.filter(
    (c) => !(isAdmin && seriesOverrides[c.idx]?.override === true)
  );
  const roomBlocked =
    roomsEnabled &&
    ((baseRoomIssue && !(isAdmin && roomOverrideConfirmed)) ||
      (!!formData.roomId && roomCheckPending) ||
      unresolvedSeriesRoomConflicts.length > 0 ||
      seriesRoomCheckPending);
  const detailsGateBlocked =
    roomBlocked ||
    // E6: patient not assigned to the target therapist (base slot or any
    // series occurrence) — create-only, cleared by the inline assign action.
    isPatientUnassignedBlocked ||
    isAssigningTherapist ||
    (!isEditing &&
      ((needsAmountGate && !zeroAmountConfirmed) ||
        (roomsEnabled &&
          accessibleRooms.length > 0 &&
          !formData.roomId &&
          !noRoomConfirmed)));

  const stepGateBlocked = (targetStep) => {
    if (targetStep === WIZARD_STEPS.SCHEDULE) return scheduleGateBlocked;
    if (targetStep === WIZARD_STEPS.DETAILS) return detailsGateBlocked;
    return false;
  };

  // ===== SMS preview build (on entering summary) =====
  const getPatientPhone = useCallback(() => {
    if (formData.isTempCustomer) {
      return formData.tempCustomerPhone?.startsWith("+972")
        ? formData.tempCustomerPhone
        : "";
    }
    return selectedPatient?.phone || selectedPatient?.mobilePhone || "";
  }, [formData.isTempCustomer, formData.tempCustomerPhone, selectedPatient]);

  const rebuildSmsPreview = useCallback(() => {
    // Edit mode never auto-sends SMS from a field update — the late "send now"
    // flow (Phase 2c) is the only SMS path there.
    if (isEditing) {
      setSmsPreviewItems([]);
      return;
    }
    setSmsPreviewItems(
      buildSmsPreview({
        formData,
        patientPhone: getPatientPhone(),
        clinicName: clinicProfile?.name || "",
        therapistSmsSettings,
        treatmentTypes,
        isPastDateTime,
        seriesItems: seriesPreview.items,
        language: smsLanguage,
      })
    );
  }, [formData, getPatientPhone, clinicProfile, therapistSmsSettings, treatmentTypes, isPastDateTime, seriesPreview.items, isEditing, smsLanguage]);

  const toggleSmsPreviewItem = useCallback((id) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => {
          if (item.id !== id) return item;
          const nextEnabled = !item.enabled;
          return {
            ...item,
            enabled: nextEnabled,
            gapOverride:
              nextEnabled && item.suppressedReason === SMS_SUPPRESS_REASON.TOO_CLOSE
                ? true
                : nextEnabled
                ? item.gapOverride
                : false,
          };
        })
      )
    );
  }, []);

  const setAllSmsEnabled = useCallback((enabled) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => ({
          ...item,
          enabled,
          gapOverride: enabled ? item.gapOverride : false,
        }))
      )
    );
  }, []);

  const updateSmsPreviewItem = useCallback((id, field, value) => {
    setSmsPreviewItems((prev) =>
      applySmsScheduleGuards(
        prev.map((item) => {
          if (item.id !== id) return item;
          const updated = {
            ...item,
            isImmediate: false,
            enabled: item.suppressedReason ? true : item.enabled,
            suppressedReason: null,
            gapOverride: false,
          };
          if (field === "date") {
            updated.scheduledDate = value;
          } else if (field === "time") {
            updated.scheduledTime = value;
          }
          return updated;
        })
      )
    );
  }, []);

  // Keep formData.sendSms in sync with the preview items (master toggle state).
  useEffect(() => {
    if (step !== WIZARD_STEPS.SUMMARY || smsPreviewItems.length === 0) return;
    const anyEnabled = smsPreviewItems.some((i) => i.enabled);
    setFormData((p) => (p.sendSms === anyEnabled ? p : { ...p, sendSms: anyEnabled }));
  }, [smsPreviewItems, step]);

  // ===== Navigation =====
  const goToStep = useCallback(
    (targetStep, { fromSummary = false } = {}) => {
      if (targetStep === WIZARD_STEPS.SUMMARY) {
        rebuildSmsPreview();
        setReturnToSummary(false);
      } else if (fromSummary) {
        setReturnToSummary(true);
      }
      setStep(targetStep);
      setMaxStepReached((prev) => Math.max(prev, targetStep));
      setStepErrors({});
    },
    [rebuildSmsPreview]
  );

  // Auto-advance after a patient pick (create flow). Runs post-commit so the
  // schedule/summary steps see the fresh formData. Multi-therapist patients
  // stay on step 1 — the therapist selector needs an explicit look.
  useEffect(() => {
    if (!autoAdvanceRef.current) return;
    autoAdvanceRef.current = false;
    if (isEditing || isMultiTherapistPatient) return;
    // Blocked time always lands on the block screen — there is no summary.
    if (formData.isBlockedTime) {
      goToStep(WIZARD_STEPS.SCHEDULE, {});
      return;
    }
    if (!formData.patientId) return;
    goToStep(
      returnToSummary ? WIZARD_STEPS.SUMMARY : WIZARD_STEPS.SCHEDULE,
      {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.patientId, formData.isBlockedTime]);

  const nextStep = useCallback(() => {
    const errors = validateStepFields(step);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    if (stepGateBlocked(step)) return;
    setStepErrors({});
    const target =
      returnToSummary && step < WIZARD_STEPS.SUMMARY
        ? WIZARD_STEPS.SUMMARY
        : step + 1;
    goToStep(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, validateStepFields, returnToSummary, goToStep, scheduleGateBlocked, detailsGateBlocked]);

  const prevStep = useCallback(() => {
    if (step > WIZARD_STEPS.PATIENT) {
      setStep(step - 1);
      setStepErrors({});
    }
  }, [step]);

  // ===== Fast path ("הכל מוכן") — create only =====
  const fastPathReady = useMemo(() => {
    if (isEditing) return false;
    const patientOk = formData.isTempCustomer
      ? formData.patientName.trim() !== "" &&
        formData.tempCustomerPhone?.startsWith("+972")
      : !!formData.patientId;
    if (!patientOk) return false;
    if (!formData.date || !formData.startTime) return false;
    if (!formData.duration || formData.duration < 1) return false;
    if (isPastDateTime && !pastDateConfirmed) return false;
    if (therapistConflictBlocked || absenceBlocked || therapistCheckPending) return false;
    if (needsAmountGate) return false;
    if (roomBlocked) return false;
    if (
      roomsEnabled &&
      accessibleRooms.length > 0 &&
      !formData.roomId &&
      !noRoomConfirmed
    )
      return false;
    return true;
  }, [formData, isEditing, isPastDateTime, pastDateConfirmed, therapistConflictBlocked, absenceBlocked, therapistCheckPending, needsAmountGate, roomBlocked, roomsEnabled, accessibleRooms.length, noRoomConfirmed]);

  const goToSummaryFastPath = useCallback(() => {
    if (!fastPathReady) return;
    const errors = {
      ...validatePatient(formData),
      ...validateSchedule(formData, { isEditing: false }),
      ...validateDetails(formData),
    };
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    goToStep(WIZARD_STEPS.SUMMARY);
  }, [fastPathReady, formData, goToStep]);

  // ===== Dirty / close guard =====
  const isFormDirty = useMemo(() => {
    if (isEditing) {
      if (!initialFormData) return false;
      return JSON.stringify(formData) !== JSON.stringify(initialFormData);
    }
    return (
      formData.patientId !== "" ||
      formData.isTempCustomer === true ||
      formData.isBlockedTime === true ||
      formData.notes !== "" ||
      formData.treatmentTypeId !== "" ||
      formData.paymentAmount !== (defaultRate ? defaultRate.toString() : "")
    );
  }, [formData, defaultRate, isEditing, initialFormData]);
  useRegisterUnsavedChanges(isFormDirty);

  const handleSafeClose = useCallback(() => {
    if (isFormDirty) setShowCloseWarning(true);
    else onClose();
  }, [isFormDirty, onClose]);

  // ===== Edit mode: cancel / restore / delete / scope helpers =====

  // Shared cancellation flow (same hook as the classic modal / calendar).
  const { startCancel, startPostpone, cancelFlowNode } = useCancelAppointmentFlow({
    onCancelled: () => refetchSeries(),
    onPostponed: (result) => {
      refetchSeries();
      if (result?.newStartTime) {
        const moved = new Date(result.newStartTime);
        setFormData((prev) => ({
          ...prev,
          date: formatLocalDateString(moved),
          startTime: moved.toTimeString().slice(0, 5),
        }));
      }
    },
  });

  const handleRestoreAppointment = useCallback(
    async (targetAptArg) => {
      const targetApt =
        targetAptArg && targetAptArg.id ? targetAptArg : liveAppointment;
      if (!targetApt?.id || restoringRowId) return;
      setRestoringRowId(targetApt.id);
      try {
        await uncancelAppointment({
          clinicId,
          appointmentId: targetApt.id,
          patientId: targetApt.patientId,
          patientName: targetApt.patientName,
          therapistId: currentTherapistId,
          therapistName: currentTherapistName,
        }).unwrap();
        showSuccess("הפגישה שוחזרה בהצלחה");
        refetchSeries();
      } catch (error) {
        console.error("[CK-3805] Error restoring cancelled appointment:", {
          errorCode: "CK-3805",
          errorMessage: error?.message,
          appointmentId: targetApt?.id,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בשחזור הפגישה");
      } finally {
        setRestoringRowId(null);
      }
    },
    [liveAppointment, restoringRowId, uncancelAppointment, clinicId, currentTherapistId, currentTherapistName, showSuccess, showError, refetchSeries]
  );

  const handleDeleteClick = useCallback(() => {
    if (!appointment) return;
    if (appointment.isRecurring) {
      setRecurringDeleteState({
        // Blocks never have a linked transaction — skip the preload query.
        linkedTransaction: appointment.isBlockedTime === true ? null : undefined,
        preloadError: null,
        deleteLinkedTransactions: false,
        includePast: false,
        isWorking: false,
      });
      setShowRecurringDeleteDialog(true);
    } else {
      setDeleteDialogWorking(false);
      setShowDeleteConfirm(true);
    }
  }, [appointment]);

  // Preload the linked transaction for the recurring-delete dialog.
  useEffect(() => {
    if (!showRecurringDeleteDialog) return undefined;
    if (!appointment?.id || !clinicId) return undefined;
    if (recurringDeleteState.linkedTransaction !== undefined) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const tx = await getTransactionByAppointment(clinicId, appointment.id);
        if (cancelled) return;
        setRecurringDeleteState((prev) => ({
          ...prev,
          linkedTransaction: tx || null,
        }));
      } catch (err) {
        console.error("[CK-3806] Error preloading transaction for recurring delete:", {
          errorCode: "CK-3806",
          errorMessage: err?.message,
          appointmentId: appointment.id,
          timestamp: new Date().toISOString(),
        });
        if (cancelled) return;
        setRecurringDeleteState((prev) => ({
          ...prev,
          linkedTransaction: prev.linkedTransaction ?? null,
          preloadError: err.message || "שגיאה בטעינת התשלום המקושר",
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRecurringDeleteDialog, appointment?.id, clinicId, recurringDeleteState.linkedTransaction]);

  const handleDeleteConfirm = useCallback(
    async (deleteLinkedTransaction) => {
      setDeleteDialogWorking(true);
      try {
        await deleteAppointment({
          clinicId,
          appointmentId: appointment.id,
          patientName: appointment.patientName,
          therapistId,
          therapistName,
          deleteLinkedTransaction: !!deleteLinkedTransaction,
        }).unwrap();
        showSuccess("הפגישה נמחקה בהצלחה");
        setShowDeleteConfirm(false);
        onClose();
      } catch (error) {
        console.error("[CK-3807] Error deleting appointment:", {
          errorCode: "CK-3807",
          errorMessage: error?.message,
          appointmentId: appointment?.id,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה במחיקת הפגישה");
      } finally {
        setDeleteDialogWorking(false);
      }
    },
    [deleteAppointment, clinicId, appointment, therapistId, therapistName, showSuccess, showError, onClose]
  );

  const handleRecurringDeleteConfirm = useCallback(async () => {
    setRecurringDeleteState((prev) => ({ ...prev, isWorking: true }));
    try {
      const result = await deleteRecurringAppointments({
        clinicId,
        appointmentId: appointment.id,
        scope: deleteScope,
        patientName: appointment.patientName,
        therapistId,
        therapistName,
        currentTherapistId,
        isAdmin,
        includePast: !!recurringDeleteState.includePast,
        deleteLinkedTransactions:
          !!recurringDeleteState.linkedTransaction &&
          !!recurringDeleteState.deleteLinkedTransactions,
      }).unwrap();

      let scopeText;
      if (deleteScope === "single") {
        scopeText = "המפגש נמחק בהצלחה";
      } else if (result.deletedCount === 1) {
        scopeText = "מפגש אחד נמחק בהצלחה";
      } else {
        scopeText = `${result.deletedCount} מפגשים נמחקו בהצלחה`;
      }
      if (result.skippedCompletedCount > 0) {
        scopeText += ` (${result.skippedCompletedCount} מפגשים שכבר התקיימו לא נמחקו)`;
      }

      showSuccess(scopeText);
      setShowRecurringDeleteDialog(false);
      onClose();
    } catch (error) {
      console.error("[CK-3808] Error deleting recurring appointments:", {
        errorCode: "CK-3808",
        errorMessage: error?.message,
        appointmentId: appointment?.id,
        timestamp: new Date().toISOString(),
      });
      if (error.code === "permission-denied") {
        showError("אין לך הרשאה למחוק פגישות אלו. פנה למנהל הקליניקה.");
      } else {
        showError(error.message || "שגיאה במחיקת הפגישות");
      }
      setRecurringDeleteState((prev) => ({ ...prev, isWorking: false }));
    }
  }, [deleteRecurringAppointments, clinicId, appointment, deleteScope, therapistId, therapistName, currentTherapistId, isAdmin, recurringDeleteState, showSuccess, showError, onClose]);

  // --- Series overview: inline row editing (mirrors the classic modal) ---

  // Reassignment of a single occurrence's therapist — admins, or anyone
  // with `calendar.manageOthersAppointments`.
  const canReassignSeriesTherapist =
    isEditing && canScheduleForOthers && allowMultipleTherapists && isMultiTherapistPatient;

  const isAppointmentPastOrFinalized = useCallback((apt) => {
    if (!apt) return false;
    if (apt.status === "completed" || apt.status === "cancelled") return true;
    if (!apt.startTime) return false;
    const startMs = new Date(apt.startTime).getTime();
    if (Number.isNaN(startMs)) return false;
    return startMs < Date.now();
  }, []);

  const handleOverviewRowEdit = useCallback((apt) => {
    const aptDate = new Date(apt.startTime);
    setEditingOverviewRow(apt.id);
    setOverviewEditValues({
      date: formatLocalDateString(aptDate),
      time: aptDate.toTimeString().slice(0, 5),
      treatmentTypeId: apt.treatmentTypeId || "",
      treatmentTypeName: apt.treatmentTypeName || "",
      therapistId: apt.therapistId || "",
      therapistName: apt.therapistName || "",
    });
  }, []);

  const cancelOverviewRowEdit = useCallback(() => {
    setEditingOverviewRow(null);
    setOverviewEditValues({});
    setRowScopeEdit(null);
    setRecurringConflicts([]);
    allowRecurringConflictsRef.current = false;
  }, []);

  const buildOverviewRowUpdateData = useCallback(
    (apt) => {
      const origDate = new Date(apt.startTime);
      const newStart = new Date(
        `${overviewEditValues.date}T${overviewEditValues.time}`
      );
      const durationMs = new Date(apt.endTime).getTime() - origDate.getTime();
      const newEnd = new Date(newStart.getTime() + durationMs);

      const updateData = {
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
      };
      if (overviewEditValues.treatmentTypeId !== undefined) {
        updateData.treatmentTypeId = overviewEditValues.treatmentTypeId || null;
        updateData.treatmentTypeName = overviewEditValues.treatmentTypeName || null;
      }
      // Therapist change is not cascaded to financial transactions /
      // treatment docs — they keep the original therapist for audit.
      if (
        canReassignSeriesTherapist &&
        overviewEditValues.therapistId &&
        overviewEditValues.therapistId !== apt.therapistId
      ) {
        updateData.therapistId = overviewEditValues.therapistId;
        updateData.therapistName = overviewEditValues.therapistName || null;
      }
      return updateData;
    },
    [overviewEditValues, canReassignSeriesTherapist]
  );

  const persistOverviewRowUpdate = useCallback(
    async (apt, { scope, allowConflicts = false } = {}) => {
      const updateData = buildOverviewRowUpdateData(apt);
      const rowMovedInTime =
        new Date(updateData.startTime).getTime() !==
        new Date(apt.startTime).getTime();

      if (apt.isRecurring && rowMovedInTime && !scope) {
        setRowScopeEdit({ apt, updateData });
        setShowScopeDialog(true);
        return;
      }

      // Single-occurrence writes (non-series rows, or scope "single") never
      // reach the server-side series conflict check, so a row that moves in
      // time or changes therapist must pass the same gates the single path
      // uses — against the ROW's therapist, excluding the row itself.
      const rowTherapistId = updateData.therapistId || apt.therapistId || therapistId;
      const rowTherapistChanged =
        !!updateData.therapistId && updateData.therapistId !== apt.therapistId;
      const rowSlotChanged = rowMovedInTime || rowTherapistChanged;
      const isSingleWrite = !(apt.isRecurring && scope && scope !== "single");

      try {
        let rowConflictIds = [];
        if (isSingleWrite && rowSlotChanged) {
          const rowStart = new Date(updateData.startTime);
          const rowEnd = new Date(updateData.endTime);
          const rowDateStr = toIsraelDateStr(rowStart);
          const rowHHMM = toIsraelHHMM(rowStart);
          const rowDurationMin = Math.max(1, Math.round((rowEnd - rowStart) / 60000));

          // Absence gate: no inline waiver on the row — admins use the full form.
          const rowAbsences = await getAbsences(clinicId, {
            from: rowDateStr,
            to: rowDateStr,
            therapistId: rowTherapistId,
          });
          if (findBlockingAbsence(rowAbsences, rowDateStr, rowHHMM, rowDurationMin)) {
            showError("המטפל/ת לא זמין/ה במועד זה (היעדרות)");
            setPastReassignConfirm({ open: false, apt: null });
            return;
          }

          // Double-booking gate: feeds the existing row refusal panel
          // (rowScopeEdit + recurringConflicts) — "עדכן בכל זאת" re-enters
          // here with allowConflicts: true.
          const { conflicts: rowConflicts } = await checkTherapistAvailabilityFn(
            clinicId,
            rowTherapistId,
            rowStart,
            rowEnd,
            { excludeAppointmentId: apt.id }
          );
          rowConflictIds = rowConflicts.map((c) => c.id).filter(Boolean);
          if (rowConflicts.length > 0 && !allowConflicts) {
            allowRecurringConflictsRef.current = false;
            setRowScopeEdit({ apt, updateData, scope: scope ?? null });
            setRecurringConflicts(
              rowConflicts.map((c) => ({
                date: rowStart.toISOString(),
                seriesIndex: apt.recurringIndex ?? null,
                selfConflict: false,
                existingAppointment: {
                  id: c.id,
                  patientName: c.patientName,
                  startTime: c.startTime,
                  endTime: c.endTime,
                },
              }))
            );
            showError("המועד החדש מתנגש עם פגישה קיימת");
            setPastReassignConfirm({ open: false, apt: null });
            return;
          }
        }

        // Scheduling-policy stamps (audit), mirroring the main submit: a slot
        // or therapist change without a waiver clears any earlier stamp; a
        // waived conflict records who waived and what it collided with.
        if (rowSlotChanged) {
          // Series scopes: the waived list is the server refusal shown in the
          // row panel; single writes: the client check above.
          const waivedIds = isSingleWrite
            ? rowConflictIds
            : recurringConflicts.map((c) => c.existingAppointment?.id).filter(Boolean);
          Object.assign(
            updateData,
            allowConflicts && canOverrideTherapistConflict && waivedIds.length > 0
              ? {
                  overrideTherapistConflict: true,
                  therapistConflictOverride: {
                    by: currentTherapistId,
                    at: new Date().toISOString(),
                    targetTherapistId: rowTherapistId,
                    conflictIds: waivedIds,
                    policy: preventTherapistDoubleBooking ? "block" : "warn",
                  },
                }
              : { overrideTherapistConflict: false, therapistConflictOverride: null },
            { overrideAbsence: false, absenceOverride: null }
          );
        }

        if (apt.isRecurring && scope && scope !== "single") {
          const seriesResult = await updateRecurringAppointments({
            clinicId,
            appointmentId: apt.id,
            updateData,
            scope,
            patientName: apt.patientName || formData.patientName,
            therapistId,
            therapistName,
            allowConflicts,
          }).unwrap();

          if (seriesResult?.hasConflicts) {
            allowRecurringConflictsRef.current = false;
            setRecurringConflicts(seriesResult.conflicts || []);
            showError(
              seriesResult.waivable
                ? "המועדים החדשים מתנגשים עם פגישות קיימות"
                : "המועד החדש מתנגש עם המועד השני בסדרה - יש לבחור יום או שעה אחרים"
            );
            if (!seriesResult.waivable) setRowScopeEdit(null);
            return;
          }

          setRecurringConflicts([]);
          allowRecurringConflictsRef.current = false;
          showSuccess(
            scope === "postpone_week"
              ? (seriesResult?.shiftedCount ?? 0) > 0
                ? `המפגש עודכן ו-${seriesResult.shiftedCount} המפגשים הבאים נדחו בשבוע`
                : "המפגש עודכן בהצלחה"
              : "המפגשים העתידיים עודכנו בהצלחה"
          );
        } else {
          await updateAppointment({
            clinicId,
            appointmentId: apt.id,
            appointmentData: updateData,
            // therapistId/Name = who performed the update (activity log);
            // the new owner is inside updateData.
            therapistId,
            therapistName,
            patientName: apt.patientName || formData.patientName,
          }).unwrap();
          showSuccess("המפגש עודכן בהצלחה");
        }
        setRowScopeEdit(null);
        setEditingOverviewRow(null);
        setOverviewEditValues({});
        setPastReassignConfirm({ open: false, apt: null });
        refetchSeries();
      } catch (error) {
        console.error("[CK-3810] Error updating series appointment:", {
          errorCode: "CK-3810",
          errorMessage: error?.message,
          appointmentId: apt.id,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בעדכון המפגש");
        setPastReassignConfirm({ open: false, apt: null });
      }
    },
    [buildOverviewRowUpdateData, updateRecurringAppointments, updateAppointment, clinicId, formData.patientName, therapistId, therapistName, currentTherapistId, canOverrideTherapistConflict, preventTherapistDoubleBooking, recurringConflicts, showSuccess, showError, refetchSeries]
  );

  const handleOverviewRowSave = useCallback(
    async (apt) => {
      if (isAppointmentPastOrFinalized(apt)) {
        setPastReassignConfirm({ open: true, apt });
        return;
      }
      await persistOverviewRowUpdate(apt);
    },
    [isAppointmentPastOrFinalized, persistOverviewRowUpdate]
  );

  const handleSeriesStatusChange = useCallback(
    async (aptId, newStatus) => {
      // Cancelling a row goes through the shared cancel flow (fees, postpone
      // alternative, linked payment) — same as every other entry point.
      if (newStatus === "cancelled") {
        const rowAppointment = seriesAppointments.find((apt) => apt.id === aptId);
        if (rowAppointment) {
          startCancel(rowAppointment);
          return;
        }
        setSeriesCancelPrompt({ open: true, aptId, reason: "", isWorking: false });
        return;
      }
      try {
        await updateAppointment({
          clinicId,
          appointmentId: aptId,
          appointmentData: {
            status: newStatus,
            ...(newStatus === "completed"
              ? { completedBy: "user", completedAt: new Date().toISOString() }
              : {}),
          },
          therapistId,
          therapistName,
          patientName: formData.patientName,
        }).unwrap();
        showSuccess("המפגש סומן כהושלם");
        refetchSeries();
      } catch (error) {
        console.error("[CK-3811] Error updating series appointment status:", {
          errorCode: "CK-3811",
          errorMessage: error?.message,
          appointmentId: aptId,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בעדכון סטטוס המפגש");
      }
    },
    [seriesAppointments, startCancel, updateAppointment, clinicId, therapistId, therapistName, formData.patientName, showSuccess, showError, refetchSeries]
  );

  const handleSeriesCancelConfirm = useCallback(async () => {
    const { aptId, reason } = seriesCancelPrompt;
    if (!aptId) return;
    setSeriesCancelPrompt((prev) => ({ ...prev, isWorking: true }));
    const trimmedReason = (reason || "").trim().slice(0, 300);
    try {
      await updateAppointment({
        clinicId,
        appointmentId: aptId,
        appointmentData: {
          status: "cancelled",
          cancelledAt: new Date().toISOString(),
          cancelledBy: "therapist",
          cancellationReason: trimmedReason || null,
          isApprovedByTherapist: true,
          isTherapistSummarizedTheMeeting: true,
          NotifyAboutThisMeetingApproval: false,
          NotifyAboutThisMeetingSummarize: false,
          NotifyAboutThisMeetingPaymentStatus: false,
        },
        therapistId,
        therapistName,
        patientName: formData.patientName,
      }).unwrap();
      showSuccess("המפגש סומן כבוטל");
      setSeriesCancelPrompt({ open: false, aptId: null, reason: "", isWorking: false });
      refetchSeries();
    } catch (error) {
      console.error("[CK-3812] Error cancelling series occurrence:", {
        errorCode: "CK-3812",
        errorMessage: error?.message,
        appointmentId: aptId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שגיאה בעדכון סטטוס המפגש");
      setSeriesCancelPrompt((prev) => ({ ...prev, isWorking: false }));
    }
  }, [seriesCancelPrompt, updateAppointment, clinicId, therapistId, therapistName, formData.patientName, showSuccess, showError, refetchSeries]);

  // ===== Communication (2c): live SMS state / status / late send =====

  // Live SMS scheduling state — the Cloud Function rewrites send times / task
  // names shortly AFTER a save; this listener keeps the chips honest.
  useEffect(() => {
    if (!isEditing || !clinicId || !appointment?.id) {
      setLiveSmsState(null);
      return undefined;
    }
    setLiveSmsState(null);
    const unsubscribe = subscribeToAppointmentSmsState(
      clinicId,
      appointment.id,
      setLiveSmsState
    );
    return unsubscribe;
  }, [isEditing, clinicId, appointment?.id]);

  const smsAppointment = useMemo(
    () => (liveSmsState ? { ...appointment, ...liveSmsState } : appointment),
    [appointment, liveSmsState]
  );

  const confirmationStatus = useMemo(() => {
    if (!isEditing) return null;
    const initialLink = confirmationLinks.find((l) => l.type === "initial");
    const reminderLink =
      confirmationLinks.find((l) => l.type === "reminder") ||
      confirmationLinks.find((l) => l.type === "recurring_reminder");

    const initialSent = smsAppointment?.whatsappInitialSent === true;
    const reminderSent =
      smsAppointment?.whatsappReminderSent === true ||
      smsAppointment?.recurring1DayReminderSent === true;
    const smsStillSendable =
      !["cancelled", "completed"].includes(smsAppointment?.status) &&
      smsAppointment?.isDeleted !== true;
    // 10-minute grace covers ASAP-clamped sends and Cloud Tasks retry lag.
    const notMissed = (scheduledAt) => {
      if (!scheduledAt) return false;
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) return false;
      return dt.getTime() > Date.now() - 10 * 60 * 1000;
    };
    const initialScheduled =
      smsStillSendable &&
      smsAppointment?.initialSmsEnabled !== false &&
      notMissed(smsAppointment?.initialSmsScheduledAt) &&
      !initialSent;
    const reminderScheduled =
      smsStillSendable &&
      smsAppointment?.reminderSmsEnabled !== false &&
      notMissed(smsAppointment?.reminderSmsScheduledAt) &&
      !reminderSent;

    return {
      initial: {
        status:
          initialLink?.response ||
          (smsAppointment?.whatsappInitialConfirmed
            ? "confirmed"
            : smsAppointment?.whatsappInitialDeclined
            ? "declined"
            : "pending"),
        sent: initialSent,
        scheduled: initialScheduled,
        scheduledAt: smsAppointment?.initialSmsScheduledAt || null,
        linkId: initialLink?.id,
      },
      reminder: {
        status:
          reminderLink?.response ||
          (smsAppointment?.whatsappReminderConfirmed
            ? "confirmed"
            : smsAppointment?.whatsappReminderDeclined
            ? "declined"
            : "pending"),
        sent: reminderSent,
        scheduled: reminderScheduled,
        scheduledAt: smsAppointment?.reminderSmsScheduledAt || null,
        linkId: reminderLink?.id,
      },
    };
  }, [isEditing, confirmationLinks, smsAppointment]);

  const noSmsRequested =
    isEditing &&
    ((smsAppointment?.initialSmsEnabled === false &&
      smsAppointment?.reminderSmsEnabled === false) ||
      (confirmationStatus &&
        !confirmationStatus.initial.sent &&
        !confirmationStatus.initial.scheduled &&
        !confirmationStatus.reminder.sent &&
        !confirmationStatus.reminder.scheduled &&
        confirmationLinks.length === 0));

  const canCancelScheduledSms = useCallback(
    (smsType) => {
      if (!isEditing || !confirmationStatus) return false;
      if (["cancelled", "completed"].includes(smsAppointment?.status)) return false;
      if (smsAppointment?.isDeleted === true) return false;
      const entry =
        smsType === "initial"
          ? confirmationStatus.initial
          : confirmationStatus.reminder;
      return entry.scheduled && !entry.sent;
    },
    [isEditing, confirmationStatus, smsAppointment]
  );

  const handleConfirmCancelScheduledSms = useCallback(async () => {
    const smsType = cancelSmsTarget?.type;
    if (!smsType) return;
    setCancelSmsTarget(null);
    setCancellingSmsType(smsType);
    try {
      await cancelScheduledSms({
        clinicId,
        appointmentId: appointment.id,
        smsType,
        therapistId,
        therapistName,
        patientName: formData.patientName || appointment?.patientName,
      }).unwrap();
      showSuccess(
        smsType === "initial"
          ? "הודעת אישור ההגעה בוטלה ולא תישלח"
          : "הודעת התזכורת בוטלה ולא תישלח"
      );
    } catch (error) {
      console.error("[CK-3813] Error cancelling scheduled SMS:", {
        errorCode: "CK-3813",
        errorMessage: error?.message || error?.data?.message,
        appointmentId: appointment?.id,
        smsType,
        timestamp: new Date().toISOString(),
      });
      showError(
        error?.data?.message || error?.message || "ביטול ההודעה נכשל, נסה/י שוב"
      );
    } finally {
      setCancellingSmsType(null);
    }
  }, [cancelSmsTarget, cancelScheduledSms, clinicId, appointment, therapistId, therapistName, formData.patientName, showSuccess, showError]);

  const handleCreateLink = useCallback(
    async (type) => {
      if (!appointment?.id || !clinicId) return;
      const startDate = new Date(appointment.startTime);
      const patientPhone = getPatientPhone();
      if (!patientPhone) {
        showError("לא נמצא מספר טלפון למטופל");
        return;
      }
      try {
        await createConfirmationLink({
          clinicId,
          linkData: {
            language: smsLanguage,
            appointmentId: appointment.id,
            type,
            phoneNumber: patientPhone,
            patientName: formData.patientName,
            appointmentDate: formatLocalDateString(startDate),
            appointmentTime: startDate.toTimeString().slice(0, 5),
            appointmentDuration: appointment.duration,
            sentAutomatically: false,
          },
        }).unwrap();
        refetchLinks();
      } catch (error) {
        console.error("[CK-3814] Error creating confirmation link:", {
          errorCode: "CK-3814",
          errorMessage: error?.message,
          appointmentId: appointment?.id,
          timestamp: new Date().toISOString(),
        });
        showError("יצירת הקישור נכשלה");
      }
    },
    [appointment, clinicId, getPatientPhone, createConfirmationLink, formData.patientName, refetchLinks, showError, smsLanguage]
  );

  const copyLink = useCallback((linkId) => {
    const url = `${window.location.origin}/confirm/${linkId}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  }, []);

  // ---- Late "send confirmation SMS now" ----
  const lateSmsStart = appointment?.startTime
    ? new Date(appointment.startTime)
    : null;
  const lateSmsPhone = isEditing ? getPatientPhone() : "";
  const canSendLateSms =
    isEditing &&
    noSmsRequested &&
    smsAppointment?.whatsappInitialSent !== true &&
    !smsAppointment?.initialSmsTaskName &&
    !["cancelled", "completed", "pending_approval"].includes(
      appointment?.status
    ) &&
    appointment?.isDeleted !== true &&
    !!lateSmsPhone &&
    lateSmsStart &&
    lateSmsStart > new Date();

  const lateSmsFutureOccurrences = useMemo(() => {
    if (!appointment?.recurringGroupId) return [];
    const now = new Date();
    return seriesAppointments
      .filter(
        (a) =>
          a.isDeleted !== true &&
          a.status !== "cancelled" &&
          a.status !== "completed" &&
          a.startTime &&
          new Date(a.startTime) > now
      )
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [appointment?.recurringGroupId, seriesAppointments]);

  const lateSmsSeriesAvailable =
    canSendLateSms &&
    appointment?.isRecurring &&
    lateSmsFutureOccurrences.length > 1 &&
    !lateSmsFutureOccurrences.some((a) => a.whatsappInitialSent === true);

  const lateSmsReminderHours = therapistSmsSettings?.reminderHoursBefore || 2;
  const lateSmsReminderOffered =
    therapistSmsSettings?.sendReminderEnabled !== false &&
    (lateSmsScope === "series" && lateSmsSeriesAvailable
      ? lateSmsFutureOccurrences.some(
          (a) =>
            new Date(a.startTime).getTime() - lateSmsReminderHours * 3600000 >
            Date.now()
        )
      : lateSmsStart &&
        lateSmsStart.getTime() - lateSmsReminderHours * 3600000 > Date.now());

  // Local wall-clock ISO (no timezone) — the server parses it as Asia/Jerusalem.
  const toLocalIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}:00`;

  const lateSmsTreatmentTypeName =
    therapistSmsSettings?.includeTreatmentTypeInSms === true &&
    formData.treatmentTypeId
      ? treatmentTypes.find((t) => t.id === formData.treatmentTypeId)?.name ||
        null
      : null;

  const lateSmsPreviewText = useMemo(() => {
    if (!showLateSmsDialog || !lateSmsStart) return "";
    const patientName = formData.patientName || appointment?.patientName || "";
    if (lateSmsScope === "series" && lateSmsSeriesAvailable) {
      const anchor = lateSmsFutureOccurrences[0];
      const anchorStart = new Date(anchor.startTime);
      const anchorDateStr = formatLocalDateString(anchorStart);
      const anchorTimeStr = anchorStart.toTimeString().slice(0, 5);
      const anchorKey = `${anchorStart.getDay()}-${anchorTimeStr}`;
      const otherSlot = lateSmsFutureOccurrences
        .map((a) => new Date(a.startTime))
        .find(
          (d) => `${d.getDay()}-${d.toTimeString().slice(0, 5)}` !== anchorKey
        );
      return buildSeriesSmsMessage({
        patientName,
        count: lateSmsFutureOccurrences.length,
        hebrewStartDate: formatSmsDate(anchorDateStr, smsLanguage),
        hasLink: false,
        language: smsLanguage,
        frequency: appointment?.recurringFrequency,
        baseDate: anchorDateStr,
        baseTime: anchorTimeStr,
        secondDay: otherSlot ? String(otherSlot.getDay()) : "",
        secondTime: otherSlot ? otherSlot.toTimeString().slice(0, 5) : "",
      });
    }
    return buildConfirmationSmsText({
      type: "initial",
      patientName,
      clinicName: clinicProfile?.name || "",
      treatmentTypeName: lateSmsTreatmentTypeName || "",
      aptDate: formatLocalDateString(lateSmsStart),
      aptTime: lateSmsStart.toTimeString().slice(0, 5),
      language: smsLanguage,
    }).message;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLateSmsDialog, lateSmsScope, lateSmsSeriesAvailable, lateSmsFutureOccurrences, formData.patientName, formData.treatmentTypeId]);

  const handleLateSmsSend = useCallback(async () => {
    if (!appointment?.id || !clinicId || !lateSmsPhone || lateSmsSending) return;
    setLateSmsSending(true);
    const createdLinkIds = [];
    try {
      const canSend = await checkSMS(false);
      if (!canSend) {
        setLateSmsSending(false);
        setShowLateSmsDialog(false);
        onClose();
        setTimeout(() => checkSMS(true), 100);
        return;
      }

      const patientName = formData.patientName || appointment.patientName || "";
      const cancelHours = therapistSmsSettings?.cancelRestrictionEnabled
        ? therapistSmsSettings.cancelRestrictionHoursBefore
        : null;
      const isSeries = lateSmsScope === "series" && lateSmsSeriesAvailable;
      const includeReminder = lateSmsReminderOffered && lateSmsIncludeReminder;

      const anchor = isSeries ? lateSmsFutureOccurrences[0] : appointment;
      const anchorStart = new Date(anchor.startTime);
      const anchorDateStr = formatLocalDateString(anchorStart);
      const anchorTimeStr = anchorStart.toTimeString().slice(0, 5);

      const baseLinkData = {
        language: smsLanguage,
        appointmentId: anchor.id,
        phoneNumber: lateSmsPhone,
        patientName,
        appointmentDate: anchorDateStr,
        appointmentTime: anchorTimeStr,
        appointmentDuration: anchor.duration || appointment.duration,
        sentAutomatically: true,
        cancelRestrictionHoursBefore: cancelHours,
        ...(isSeries
          ? {
              isRecurringSeries: true,
              recurringCount: lateSmsFutureOccurrences.length,
            }
          : {}),
      };

      // Reuse an unused active initial link when it belongs to the anchor.
      let initialLinkId = null;
      const reusableInitial =
        anchor.id === appointment.id
          ? confirmationLinks.find(
              (l) => l.type === "initial" && l.isActive !== false && !l.response
            )
          : null;
      if (reusableInitial && !isSeries) {
        initialLinkId = reusableInitial.id;
      } else {
        if (reusableInitial && isSeries) {
          await deactivateConfirmationLink({
            clinicId,
            linkId: reusableInitial.id,
            appointmentId: appointment.id,
          });
        }
        const created = await createConfirmationLink({
          clinicId,
          linkData: { ...baseLinkData, type: "initial" },
        }).unwrap();
        initialLinkId = created.id;
        createdLinkIds.push({ linkId: created.id, appointmentId: anchor.id });
      }

      // Single scope + reminder: the reminder task handler requires an active
      // reminder link — create up front so a send failure rolls back together.
      if (!isSeries && includeReminder) {
        const reusableReminder = confirmationLinks.find(
          (l) => l.type === "reminder" && l.isActive !== false && !l.response
        );
        if (!reusableReminder) {
          const createdReminder = await createConfirmationLink({
            clinicId,
            linkData: { ...baseLinkData, type: "reminder" },
          }).unwrap();
          createdLinkIds.push({
            linkId: createdReminder.id,
            appointmentId: anchor.id,
          });
        }
      }

      const smsResult = await sendAppointmentSMS({
        language: smsLanguage,
        name: patientName,
        date: anchorDateStr,
        time: anchorTimeStr,
        linkId: initialLinkId,
        phone: lateSmsPhone,
        clinicName: clinicProfile?.name,
        treatmentTypeName: isSeries ? null : lateSmsTreatmentTypeName,
        customMessage: isSeries ? lateSmsPreviewText : null,
        videoMeetLink:
          appointment?.isOnlineMeeting === true && appointment?.videoMeetLinkId
            ? `https://clinikit.co.il/meet/${appointment.videoMeetLinkId}`
            : null,
        messageType: isSeries
          ? "appointment_series"
          : appointment?.isOnlineMeeting === true && appointment?.videoMeetLinkId
          ? "appointment_initial_video"
          : "appointment_initial",
        appointmentId: appointment?.id || null,
        patientId: appointment?.patientId || null,
      });

      if (!smsResult.success) {
        for (const { linkId, appointmentId } of createdLinkIds) {
          await deactivateConfirmationLink({ clinicId, linkId, appointmentId });
        }
        console.error("[CK-3815] Late confirmation SMS send failed:", {
          errorCode: "CK-3815",
          errorMessage: smsResult.error,
          appointmentId: appointment.id,
          scope: lateSmsScope,
          timestamp: new Date().toISOString(),
        });
        showError("שליחת ה-SMS נכשלה, נסה/י שוב");
        setLateSmsSending(false);
        return;
      }

      refreshUsageCache();

      await updateAppointment({
        clinicId,
        appointmentId: anchor.id,
        appointmentData: {
          whatsappInitialSent: true,
          initialSmsEnabled: true,
          ...(!isSeries && includeReminder
            ? {
                reminderSmsEnabled: true,
                reminderSmsScheduledAt: toLocalIso(
                  new Date(
                    anchorStart.getTime() - lateSmsReminderHours * 3600000
                  )
                ),
              }
            : {}),
        },
        therapistId,
        therapistName,
        patientName,
      }).unwrap();

      // Series + reminders: enable a reminder per future occurrence whose send
      // time is still ahead (link must exist for the task handler).
      let reminderFailures = 0;
      if (isSeries && includeReminder) {
        for (const occ of lateSmsFutureOccurrences) {
          const occStart = new Date(occ.startTime);
          const occReminderTime = new Date(
            occStart.getTime() - lateSmsReminderHours * 3600000
          );
          if (occReminderTime <= new Date()) continue;
          try {
            await createConfirmationLink({
              clinicId,
              linkData: {
                language: smsLanguage,
                appointmentId: occ.id,
                type: "reminder",
                phoneNumber: lateSmsPhone,
                patientName,
                appointmentDate: formatLocalDateString(occStart),
                appointmentTime: occStart.toTimeString().slice(0, 5),
                appointmentDuration: occ.duration || appointment.duration,
                sentAutomatically: true,
                cancelRestrictionHoursBefore: cancelHours,
              },
            }).unwrap();
            await updateAppointment({
              clinicId,
              appointmentId: occ.id,
              appointmentData: {
                reminderSmsEnabled: true,
                reminderSmsScheduledAt: toLocalIso(occReminderTime),
              },
              therapistId,
              therapistName,
              patientName,
            }).unwrap();
          } catch (occError) {
            reminderFailures += 1;
            console.error("[CK-3816] Late series reminder enable failed:", {
              errorCode: "CK-3816",
              errorMessage: occError?.message,
              appointmentId: occ.id,
              timestamp: new Date().toISOString(),
            });
          }
        }
      }

      refetchLinks();
      if (isSeries) refetchSeries();
      if (reminderFailures > 0) {
        showError(
          `ההודעה נשלחה, אך תזמון התזכורת נכשל עבור ${reminderFailures} מפגשים`
        );
      } else {
        showSuccess(
          isSeries
            ? includeReminder
              ? "הודעת הסדרה נשלחה ותזכורות תוזמנו"
              : "הודעת הסדרה נשלחה למטופל/ת"
            : "הודעת ה-SMS נשלחה למטופל/ת"
        );
      }
      setShowLateSmsDialog(false);
    } catch (error) {
      console.error("[CK-3815] Late confirmation SMS flow failed:", {
        errorCode: "CK-3815",
        errorMessage: error?.message,
        appointmentId: appointment?.id,
        scope: lateSmsScope,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשליחת ה-SMS");
    } finally {
      setLateSmsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointment, clinicId, lateSmsPhone, lateSmsSending, checkSMS,
    formData.patientName, therapistSmsSettings, lateSmsScope,
    lateSmsSeriesAvailable, lateSmsReminderOffered, lateSmsIncludeReminder,
    lateSmsFutureOccurrences, confirmationLinks, createConfirmationLink,
    deactivateConfirmationLink, clinicProfile, lateSmsTreatmentTypeName,
    lateSmsPreviewText, lateSmsReminderHours, refreshUsageCache,
    updateAppointment, therapistId, therapistName, refetchLinks,
    refetchSeries, showSuccess, showError, onClose,
  ]);

  // ---- Manual "send reminder now" (WhatsApp-first / SMS fallback) ----
  // Eligibility is the shared rule (utils/appointmentDisplay) evaluated on the
  // LIVE doc so a confirmation arriving while the popup is open hides the
  // button. The Cloud Function re-checks everything (incl. phone + cooldown).
  const canSendReminderNow =
    isEditing && canSendManualReminder(smsAppointment);
  const reminderNowAutoPending =
    !!confirmationStatus?.reminder?.scheduled;
  const reminderNowLastSentAt = useMemo(() => {
    const raw = smsAppointment?.manualReminderLastSentAt;
    if (!raw) return null;
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }, [smsAppointment?.manualReminderLastSentAt]);

  const handleSendReminderNow = useCallback(async () => {
    if (!appointment?.id || !clinicId || reminderNowSending) return;
    setReminderNowSending(true);
    const patientName = formData.patientName || appointment.patientName || "";
    try {
      const result = await sendReminderNow({
        clinicId,
        appointmentId: appointment.id,
        therapistId,
        therapistName,
        patientName,
      }).unwrap();
      refreshUsageCache();
      refetchLinks();
      setShowReminderNowDialog(false);
      showSuccess(
        result?.channel === "whatsapp"
          ? "התזכורת נשלחה למטופל/ת בוואטסאפ"
          : "התזכורת נשלחה למטופל/ת ב-SMS"
      );
    } catch (error) {
      const message =
        error?.message || error?.data?.message || "שליחת התזכורת נכשלה";
      console.error("[CK-3827] Manual reminder send failed (wizard):", {
        errorCode: "CK-3827",
        errorMessage: message,
        appointmentId: appointment?.id,
        timestamp: new Date().toISOString(),
      });
      showError(message);
    } finally {
      setReminderNowSending(false);
    }
  }, [
    appointment, clinicId, reminderNowSending, sendReminderNow, therapistId,
    therapistName, formData.patientName, refreshUsageCache, refetchLinks,
    showSuccess, showError,
  ]);

  // --- Scope-dialog helper data (mirrors the classic modal) ---
  const scopeNewStart = rowScopeEdit
    ? new Date(rowScopeEdit.updateData.startTime)
    : formData.date && formData.startTime
    ? new Date(`${formData.date}T${formData.startTime}`)
    : null;

  // The scope dialog answers for either the main form or a series-overview row.
  const scopeApt = rowScopeEdit?.apt || appointment;

  const editDateChanged = Boolean(
    isEditing &&
      scopeApt?.startTime &&
      scopeNewStart &&
      formatLocalDateString(new Date(scopeApt.startTime)) !==
        formatLocalDateString(scopeNewStart)
  );

  // Occurrences after the edited one that a "this + future" edit would move.
  const laterSeriesMeetings = useMemo(() => {
    if (!isEditing || !scopeApt?.startTime) return [];
    const originalStartMs = new Date(scopeApt.startTime).getTime();
    return seriesAppointments.filter((apt) => {
      if (apt.id === scopeApt.id) return false;
      if (apt.isDeleted === true) return false;
      if (apt.status === "cancelled") return false;
      return new Date(apt.startTime).getTime() > originalStartMs;
    });
  }, [isEditing, scopeApt, seriesAppointments]);

  // Twice-weekly: the server shifts only the edited slot's meetings.
  const laterShiftedMeetings = useMemo(() => {
    if (scopeApt?.recurringFrequency !== RECURRING_FREQUENCY.TWICE_WEEKLY) {
      return laterSeriesMeetings;
    }
    const targetSlot = scopeApt?.recurringSlot || null;
    const targetDay = scopeApt?.startTime
      ? new Date(scopeApt.startTime).getDay()
      : null;
    return laterSeriesMeetings.filter((apt) =>
      apt.recurringSlot && targetSlot
        ? apt.recurringSlot === targetSlot
        : new Date(apt.startTime).getDay() === targetDay
    );
  }, [scopeApt, laterSeriesMeetings]);

  const postponableMeetings = useMemo(
    () =>
      laterSeriesMeetings.filter(
        (apt) =>
          apt.status !== "completed" &&
          new Date(apt.startTime).getTime() > Date.now()
      ),
    [laterSeriesMeetings]
  );

  const canPostponeSeries = editDateChanged && postponableMeetings.length > 0;

  const postponedNextMeetingDate = useMemo(() => {
    if (!canPostponeSeries) return null;
    const next = new Date(
      Math.min(...postponableMeetings.map((apt) => new Date(apt.startTime).getTime()))
    );
    next.setDate(next.getDate() + 7);
    return next;
  }, [canPostponeSeries, postponableMeetings]);

  const postponedLastMeetingDate = useMemo(() => {
    if (!canPostponeSeries) return null;
    const last = new Date(
      Math.max(...postponableMeetings.map((apt) => new Date(apt.startTime).getTime()))
    );
    last.setDate(last.getDate() + 7);
    return last;
  }, [canPostponeSeries, postponableMeetings]);

  // Moving the date back onto the original day retires the postpone option.
  useEffect(() => {
    if (!canPostponeSeries && editScope === "postpone_week") {
      setEditScope("single");
    }
  }, [canPostponeSeries, editScope]);

  // Postpone-this-meeting shortcut (delete dialog) — only while still ahead.
  const canPostponeAppointment = useMemo(() => {
    if (!appointment?.isRecurring || !appointment.startTime) return false;
    const moved = new Date(appointment.startTime);
    moved.setDate(moved.getDate() + 7);
    return moved.getTime() > Date.now();
  }, [appointment]);

  // ===== Submit (ported from AppointmentModal create branches) =====
  const submit = useCallback(async () => {
    if (isSubmitting) return;

    // Final safety net — the steps already validated, but jumping around the
    // wizard must never weaken the rules or the confirmation gates.
    const allErrors = {
      ...validatePatient(formData),
      ...validateSchedule(formData, { isEditing: false }),
      ...validateDetails(formData),
      ...validateExternalConflicts({
        hasBlockingConflicts,
        roomsEnabled,
        roomId: formData.roomId,
        roomConflicts: isAdmin && roomOverrideConfirmed ? [] : roomConflicts,
        roomAvailabilityError:
          isAdmin && roomOverrideConfirmed ? null : roomAvailabilityError,
      }),
    };
    if (Object.keys(allErrors).length > 0) {
      setStepErrors(allErrors);
      const firstStep = allErrors.patientId || allErrors.tempCustomerName || allErrors.tempCustomerPhone
        ? WIZARD_STEPS.PATIENT
        : allErrors.date || allErrors.startTime || allErrors.duration ||
          allErrors.seriesTotalCount || allErrors.seriesStartIndex ||
          allErrors.recurringSecondDay || allErrors.conflicts
        ? WIZARD_STEPS.SCHEDULE
        : WIZARD_STEPS.DETAILS;
      setStep(firstStep);
      setReturnToSummary(true);
      return;
    }
    if (scheduleGateBlocked) {
      setStep(WIZARD_STEPS.SCHEDULE);
      setReturnToSummary(true);
      return;
    }
    if (detailsGateBlocked) {
      setStep(WIZARD_STEPS.DETAILS);
      setReturnToSummary(true);
      return;
    }

    const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const endDateTime = new Date(
      startDateTime.getTime() + formData.duration * 60000
    );
    const paymentAmount = formData.paymentAmount
      ? parseFloat(formData.paymentAmount)
      : null;

    // Edit mode: no SMS scheduling fields in the update payload (mirrors the
    // classic modal, whose preview items exist only in the create flow).
    const smsSchedulingData = isEditing
      ? {}
      : getSchedulingFromPreview(smsPreviewItems, formData.isRecurring);

    const selectedTreatmentType = formData.treatmentTypeId
      ? treatmentTypes.find((t) => t.id === formData.treatmentTypeId)
      : null;

    const appointmentData = {
      patientId: formData.isTempCustomer ? null : formData.patientId,
      patientName: formData.patientName,
      isTempCustomer: formData.isTempCustomer === true,
      tempCustomerPhone: formData.isTempCustomer
        ? formData.tempCustomerPhone
        : null,
      therapistName,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      duration: parseInt(formData.duration, 10),
      notes: formData.notes,
      treatmentTypeId: formData.treatmentTypeId || null,
      treatmentTypeName: selectedTreatmentType?.name || null,
      paymentAmount,
      roomId: roomsEnabled && formData.roomId ? formData.roomId : null,
      // Online video meeting: keep the existing link id on edit; mint one when
      // the toggle turns on for the first time. Recurring creation regenerates
      // a unique id per occurrence in createRecurringAppointments.
      isOnlineMeeting:
        onlineMeetingsEnabled &&
        !formData.isBlockedTime &&
        formData.isOnlineMeeting === true,
      videoMeetLinkId:
        appointment?.videoMeetLinkId ||
        (onlineMeetingsEnabled && formData.isOnlineMeeting === true
          ? generateVideoMeetLinkId()
          : null),
      // Past-date only blocks the end-meeting SMS on CREATE — the classic
      // modal never treats an edited appointment as "past" here.
      sendEndMeetingSms:
        isEndMeetingSmsEnabled &&
        !(isPastDateTime && !isEditing) &&
        formData.sendEndMeetingSms === true,
      ...(isPastDateTime && !isEditing
        ? { isPastAppointment: true, status: "completed" }
        : {}),
      ...(isAdmin && roomOverrideConfirmed && baseRoomIssue
        ? {
            roomOverride: {
              by: therapistId,
              at: new Date().toISOString(),
              reason: roomConflicts.length > 0 ? "taken" : roomAvailabilityReason?.reason || "availability",
            },
          }
        : {}),
      // Scheduling-policy waivers (audit). Single: the inline gate; series:
      // the "צור בכל זאת" re-submit (allowRecurringConflictsRef).
      // A slot change WITHOUT a waiver clears any earlier stamp — a meeting
      // moved to a free slot must not keep carrying overrideX: true.
      ...((slotChanged && overlappingAppointment && overlapConfirmed && canOverrideTherapistConflict) ||
      (formData.isRecurring && !isEditing && allowRecurringConflictsRef.current)
        ? {
            overrideTherapistConflict: true,
            therapistConflictOverride: {
              by: currentTherapistId,
              at: new Date().toISOString(),
              targetTherapistId: therapistId,
              conflictIds: formData.isRecurring && allowRecurringConflictsRef.current
                ? recurringConflicts.map((c) => c.existingAppointment?.id).filter(Boolean)
                : therapistConflicts.map((c) => c.id),
              policy: preventTherapistDoubleBooking ? "block" : "warn",
            },
          }
        : slotChanged
          ? { overrideTherapistConflict: false, therapistConflictOverride: null }
          : {}),
      ...(slotChanged && therapistAbsence && absenceConfirmed && canOverrideAbsence
        ? {
            overrideAbsence: true,
            absenceOverride: {
              by: currentTherapistId,
              at: new Date().toISOString(),
              targetTherapistId: therapistId,
              absenceId: therapistAbsence.id,
              policy: blockBookingOnAbsence ? "block" : "warn",
            },
          }
        : slotChanged
          ? { overrideAbsence: false, absenceOverride: null }
          : {}),
      ...smsSchedulingData,
    };

    // Punch-card args (top-level mutation args, never persisted on the doc).
    const wasPunched = isEditing && appointment?.isPunchCard === true;
    let punchArgs = {};
    if (punchCardsEnabled && showPunchOption) {
      if (!isEditing) {
        if (usePunchCard && activePunchCard) {
          punchArgs = {
            punchCard: {
              cardId: activePunchCard.id,
              patientId: formData.patientId,
            },
          };
        }
      } else if (usePunchCard && !wasPunched && activePunchCard) {
        punchArgs = {
          punchAction: "punch",
          punchCard: {
            cardId: activePunchCard.id,
            patientId: formData.patientId,
          },
        };
      } else if (!usePunchCard && wasPunched) {
        punchArgs = {
          punchAction: "restore",
          punchCard: {
            cardId: appointment.punchCardId,
            patientId: appointment.patientId,
          },
        };
      }
    }

    try {
      setIsSubmitting(true);
      setSmsError(null);
      if (!isEditing) dispatch(showGlobalLoader("יוצר פגישה..."));

      if (isEditing) {
        if (appointment?.isRecurring && !pendingEditDataRef.current) {
          pendingEditDataRef.current = appointmentData;
          // Ask for the scope unless the therapist already chose it and is
          // now waiving a conflict.
          if (!reuseEditScopeRef.current) {
            setShowScopeDialog(true);
            setIsSubmitting(false);
            return;
          }
        }

        if (appointment?.isRecurring && pendingEditDataRef.current) {
          const seriesResult = await updateRecurringAppointments({
            clinicId,
            appointmentId: appointment.id,
            updateData: pendingEditDataRef.current,
            scope: editScope,
            patientName: formData.patientName,
            therapistId,
            therapistName,
            allowConflicts: allowRecurringConflictsRef.current,
          }).unwrap();

          if (seriesResult?.hasConflicts) {
            setIsSubmitting(false);
            reuseEditScopeRef.current = false;
            if (seriesResult.waivable) {
              allowRecurringConflictsRef.current = false;
              setRecurringConflicts(seriesResult.conflicts || []);
              showError("המועדים החדשים מתנגשים עם פגישות קיימות");
            } else {
              pendingEditDataRef.current = null;
              allowRecurringConflictsRef.current = false;
              setRecurringConflicts(seriesResult.conflicts || []);
              showError(
                "המועד החדש מתנגש עם המועד השני בסדרה - יש לבחור יום או שעה אחרים"
              );
            }
            setStep(WIZARD_STEPS.SCHEDULE);
            setReturnToSummary(true);
            return;
          }
          pendingEditDataRef.current = null;
          reuseEditScopeRef.current = false;
          setRecurringConflicts([]);
          allowRecurringConflictsRef.current = false;
          const scopeLabel =
            editScope === "single"
              ? "הפגישה עודכנה בהצלחה"
              : editScope === "postpone_week"
              ? (seriesResult?.shiftedCount ?? 0) > 0
                ? `הפגישה עודכנה ו-${seriesResult.shiftedCount} המפגשים הבאים נדחו בשבוע`
                : "הפגישה עודכנה בהצלחה"
              : "הפגישות העתידיות עודכנו בהצלחה";
          showSuccess(scopeLabel);
        } else {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData,
            therapistId,
            therapistName,
            patientName: formData.patientName,
            ...punchArgs,
          }).unwrap();

          // Temp customer phone edited — confirmation links snapshot the phone
          // at creation, so sync them.
          if (
            formData.isTempCustomer &&
            formData.tempCustomerPhone?.startsWith("+972") &&
            formData.tempCustomerPhone !== appointment.tempCustomerPhone
          ) {
            try {
              await syncConfirmationLinkPhone(
                clinicId,
                appointment.id,
                formData.tempCustomerPhone
              );
            } catch (syncError) {
              console.error("[CK-3809] Error syncing temp customer link phone:", {
                errorCode: "CK-3809",
                errorMessage: syncError?.message,
                appointmentId: appointment.id,
                timestamp: new Date().toISOString(),
              });
            }
          }
          showSuccess("הפגישה עודכנה בהצלחה");
        }

        onClose();
        return;
      }

      if (formData.isRecurring) {
        const overridesMap =
          Object.keys(seriesOverrides).length > 0 ? seriesOverrides : null;
        const recurringConfig = {
          frequency: formData.recurringFrequency,
          totalCount: parseInt(formData.seriesTotalCount, 10),
          startIndex: parseInt(formData.seriesStartIndex, 10) || 1,
          overrides: overridesMap,
          allowConflicts: allowRecurringConflictsRef.current,
          secondSlot:
            formData.recurringFrequency === RECURRING_FREQUENCY.TWICE_WEEKLY &&
            formData.recurringSecondDay !== "" &&
            formData.recurringSecondTime
              ? {
                  dayOfWeek: Number(formData.recurringSecondDay),
                  time: formData.recurringSecondTime,
                }
              : null,
        };

        const result = await createRecurringAppointments({
          clinicId,
          therapistId,
          therapistName,
          appointmentData,
          recurringConfig,
          ...punchArgs,
        }).unwrap();

        if (
          result.punchedCount > 0 &&
          result.punchedCount < (result.totalCreated || 0)
        ) {
          showSuccess(
            `${result.punchedCount} פגישות נוקבו מהכרטיסיה, היתר נוצרו כפגישות רגילות`
          );
        }

        if (result.hasConflicts) {
          dispatch(hideGlobalLoader());
          setIsSubmitting(false);
          const conflicts = result.conflicts || [];
          allowRecurringConflictsRef.current = false;
          setRecurringConflicts(conflicts);
          const isBlocking = conflicts.some((c) => c.selfConflict);
          showError(
            isBlocking
              ? `נמצאו ${conflicts.length} התנגשויות בתוך הסדרה עצמה - יש לשנות את מועדי הסדרה`
              : `נמצאו ${conflicts.length} התנגשויות עם פגישות קיימות - ראה/י פירוט בשלב המועד`
          );
          setStep(WIZARD_STEPS.SCHEDULE);
          setReturnToSummary(true);
          return;
        }

        // Series confirmation SMS — one summary message to the patient.
        const seriesInitialPreview = smsPreviewItems.find(
          (i) => i.id === "series-initial"
        );
        const shouldSendSeriesSms = seriesInitialPreview
          ? seriesInitialPreview.enabled
          : false;
        const isSeriesImmediate = seriesInitialPreview
          ? seriesInitialPreview.isImmediate !== false
          : true;
        if (
          shouldSendSeriesSms &&
          isSeriesImmediate &&
          result.appointments?.length > 0
        ) {
          const patientPhone = getPatientPhone();
          if (patientPhone) {
            const canSendSms = await checkSMS(false);
            if (!canSendSms) {
              dispatch(hideGlobalLoader());
              setIsSubmitting(false);
              onClose();
              setTimeout(() => checkSMS(true), 100);
              return;
            }

            const recurringCancelHours = therapistSmsSettings?.cancelRestrictionEnabled
              ? therapistSmsSettings.cancelRestrictionHoursBefore
              : null;
            try {
              const firstAppt = result.appointments[0];
              const linkData = {
            language: smsLanguage,
                language: smsLanguage,
                appointmentId: firstAppt.id,
                phoneNumber: patientPhone,
                patientName: formData.patientName,
                appointmentDate: formData.date,
                appointmentTime: formData.startTime,
                appointmentDuration: parseInt(formData.duration, 10),
                sentAutomatically: true,
                isRecurringSeries: true,
                recurringCount: result.totalCreated,
                cancelRestrictionHoursBefore: recurringCancelHours,
              };

              const initialLink = await createConfirmationLink({
                clinicId,
                linkData: { ...linkData, type: "initial" },
              }).unwrap();

              await sendAppointmentSMS({
                language: smsLanguage,
                name: formData.patientName,
                date: formData.date,
                time: formData.startTime,
                linkId: initialLink.id,
                phone: patientPhone,
                clinicName: clinicProfile?.name,
                videoMeetLink:
                  firstAppt?.isOnlineMeeting === true && firstAppt?.videoMeetLinkId
                    ? `https://clinikit.co.il/meet/${firstAppt.videoMeetLinkId}`
                    : null,
                messageType: "appointment_series",
                appointmentId: firstAppt?.id || null,
                patientId: formData.patientId || null,
                templateVars: {
                  count: result.totalCreated,
                  frequencyLabel:
                    FREQUENCY_LABELS[smsLanguage === "en" ? "en" : "he"]?.[formData.recurringFrequency] || "",
                  startDate: formatSmsDate(formData.date, smsLanguage),
                },
                customMessage: buildSeriesSmsMessage({
                  patientName: formData.patientName,
                  count: result.totalCreated,
                  hebrewStartDate: formatSmsDate(formData.date, smsLanguage),
                  hasLink: true,
                  language: smsLanguage,
                  frequency: formData.recurringFrequency,
                  baseDate: formData.date,
                  baseTime: formData.startTime,
                  secondDay: formData.recurringSecondDay,
                  secondTime: formData.recurringSecondTime,
                }),
              });

              refreshUsageCache();

              await updateAppointment({
                clinicId,
                appointmentId: firstAppt.id,
                appointmentData: { whatsappInitialSent: true },
              }).unwrap();
            } catch (smsErr) {
              console.error("[CK-3802] Error sending recurring series SMS:", {
                errorCode: "CK-3802",
                errorMessage: smsErr?.message,
                timestamp: new Date().toISOString(),
              });
            }
          }
        }

        // Series of online meetings without a confirmation SMS — send a
        // dedicated video-link SMS for the first meeting (later occurrences
        // get their own links in the reminder SMS).
        if (
          !shouldSendSeriesSms &&
          appointmentData.isOnlineMeeting === true &&
          formData.sendVideoLinkSms === true &&
          result.appointments?.length > 0
        ) {
          const seriesFirstAppt = result.appointments[0];
          const videoPhone = getPatientPhone();
          if (videoPhone && seriesFirstAppt?.videoMeetLinkId) {
            const canSendVideoSms = await checkSMS(false);
            if (canSendVideoSms) {
              const videoSmsResult = await sendAppointmentSMS({
                language: smsLanguage,
                name: formData.patientName,
                date: formData.date,
                time: formData.startTime,
                linkId: null,
                rawLink: `https://clinikit.co.il/meet/${seriesFirstAppt.videoMeetLinkId}`,
                messageType: "appointment_series_video",
                appointmentId: seriesFirstAppt?.id || null,
                patientId: formData.patientId || null,
                phone: videoPhone,
                clinicName: clinicProfile?.name,
                customMessage: `היי ${formData.patientName}, נקבעה לך סדרת פגישות וידאו אונליין. קישור להצטרפות למפגש הראשון ב-${formatHebrewDate(formData.date)} בשעה ${formData.startTime}:`,
              });
              if (!videoSmsResult.success) {
                console.error("[CK-3971] Series video link SMS send failed:", {
                  errorCode: "CK-3971",
                  errorMessage: videoSmsResult.error,
                  timestamp: new Date().toISOString(),
                });
                showError("הסדרה נוצרה אך שליחת קישור הווידאו נכשלה");
              } else {
                refreshUsageCache();
              }
            }
          }
        }
      } else {
        const isPastAppt = isPastDateTime && pastDateConfirmed;
        const newAppointment = await createAppointment({
          clinicId,
          therapistId,
          therapistName,
          appointmentData,
          isAdminScheduling,
          scheduledByAdminId: isAdminScheduling ? scheduledByAdminId : null,
          scheduledByAdminName: isAdminScheduling ? scheduledByAdminName : null,
          ...punchArgs,
        }).unwrap();

        if (punchArgs.punchCard && newAppointment?.punchFallback === true) {
          showError("לא נותרו ניקובים בכרטיסיה — הפגישה נוצרה כפגישה רגילה");
        }

        const patientPhone = getPatientPhone();
        const cancelHours = therapistSmsSettings?.cancelRestrictionEnabled
          ? therapistSmsSettings.cancelRestrictionHoursBefore
          : null;
        const initialPreview = smsPreviewItems.find(
          (i) => i.id === "single-initial"
        );
        const reminderPreview = smsPreviewItems.find(
          (i) => i.id === "single-reminder"
        );
        const shouldSendInitial = initialPreview ? initialPreview.enabled : false;
        const shouldSendReminder = reminderPreview ? reminderPreview.enabled : false;
        const isInitialImmediate = initialPreview
          ? initialPreview.isImmediate !== false
          : true;
        const anySmsEnabled = shouldSendInitial || shouldSendReminder;

        if (!isPastAppt && patientPhone && newAppointment?.id && anySmsEnabled) {
          const linkData = {
            language: smsLanguage,
            appointmentId: newAppointment.id,
            phoneNumber: patientPhone,
            patientName: formData.patientName,
            appointmentDate: formData.date,
            appointmentTime: formData.startTime,
            appointmentDuration: parseInt(formData.duration, 10),
            sentAutomatically: shouldSendInitial && isInitialImmediate,
            cancelRestrictionHoursBefore: cancelHours,
          };

          const [initialLink] = await Promise.all([
            shouldSendInitial
              ? createConfirmationLink({
                  clinicId,
                  linkData: { ...linkData, type: "initial" },
                }).unwrap()
              : Promise.resolve(null),
            shouldSendReminder
              ? createConfirmationLink({
                  clinicId,
                  linkData: { ...linkData, type: "reminder" },
                }).unwrap()
              : Promise.resolve(null),
          ]);

          if (shouldSendInitial && isInitialImmediate && initialLink?.id) {
            const canSendSms = await checkSMS(false);
            if (!canSendSms) {
              dispatch(hideGlobalLoader());
              setIsSubmitting(false);
              onClose();
              setTimeout(() => checkSMS(true), 100);
              return;
            }

            const smsTreatmentTypeName =
              therapistSmsSettings?.includeTreatmentTypeInSms &&
              formData.treatmentTypeId
                ? treatmentTypes.find((t) => t.id === formData.treatmentTypeId)
                    ?.name || null
                : null;
            const smsResult = await sendAppointmentSMS({
              language: smsLanguage,
              name: formData.patientName,
              date: formData.date,
              time: formData.startTime,
              linkId: initialLink.id,
              phone: patientPhone,
              clinicName: clinicProfile?.name,
              treatmentTypeName: smsTreatmentTypeName,
              videoMeetLink:
                newAppointment?.isOnlineMeeting === true &&
                newAppointment?.videoMeetLinkId
                  ? `https://clinikit.co.il/meet/${newAppointment.videoMeetLinkId}`
                  : null,
              messageType:
                newAppointment?.isOnlineMeeting === true && newAppointment?.videoMeetLinkId
                  ? "appointment_initial_video"
                  : "appointment_initial",
              appointmentId: newAppointment?.id || null,
              patientId: formData.patientId || null,
            });

            if (!smsResult.success) {
              console.error("[CK-3803] Wizard SMS sending failed:", {
                errorCode: "CK-3803",
                errorMessage: smsResult.error,
                timestamp: new Date().toISOString(),
              });
              dispatch(hideGlobalLoader());
              setSmsError(smsResult.error);
              setIsSubmitting(false);
              return;
            }

            refreshUsageCache();

            await updateAppointment({
              clinicId,
              appointmentId: newAppointment.id,
              appointmentData: { whatsappInitialSent: true },
            }).unwrap();
          }
        }

        // Online meeting without a confirmation SMS — the patient would never
        // receive the join link, so send a dedicated video-link SMS instead.
        if (
          !isPastAppt &&
          patientPhone &&
          newAppointment?.isOnlineMeeting === true &&
          newAppointment?.videoMeetLinkId &&
          !shouldSendInitial &&
          formData.sendVideoLinkSms === true
        ) {
          const canSendVideoSms = await checkSMS(false);
          if (canSendVideoSms) {
            const videoSmsResult = await sendAppointmentSMS({
              language: smsLanguage,
              name: formData.patientName,
              date: formData.date,
              time: formData.startTime,
              linkId: null,
              rawLink: `https://clinikit.co.il/meet/${newAppointment.videoMeetLinkId}`,
              messageType: "appointment_video_link",
              appointmentId: newAppointment?.id || null,
              patientId: formData.patientId || null,
              phone: patientPhone,
              clinicName: clinicProfile?.name,
              customMessage: `היי ${formData.patientName}, נקבעה לך פגישת וידאו אונליין ב-${formatHebrewDate(formData.date)} בשעה ${formData.startTime}. קישור להצטרפות לשיחה:`,
            });
            if (!videoSmsResult.success) {
              console.error("[CK-3971] Video link SMS send failed:", {
                errorCode: "CK-3971",
                errorMessage: videoSmsResult.error,
                appointmentId: newAppointment.id,
                timestamp: new Date().toISOString(),
              });
              showError("הפגישה נוצרה אך שליחת קישור הווידאו נכשלה");
            } else {
              refreshUsageCache();
            }
          }
        }
      }

      dispatch(hideGlobalLoader());
      showSuccess(
        formData.isRecurring
          ? "סדרת המפגשים נוצרה בהצלחה"
          : "הפגישה נוצרה בהצלחה"
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      dispatch(hideGlobalLoader());
      console.error("[CK-3800] Error creating appointment from wizard:", {
        errorCode: "CK-3800",
        errorMessage: error?.message,
        clinicId,
        isRecurring: formData.isRecurring,
        timestamp: new Date().toISOString(),
      });
      setSmsError(error.message || "שגיאה בשמירת הפגישה");
      showError("שגיאה בשמירת הפגישה");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting, formData, hasBlockingConflicts, roomsEnabled, roomConflicts,
    roomAvailabilityError, roomAvailabilityReason, roomOverrideConfirmed,
    baseRoomIssue, isAdmin, scheduleGateBlocked, detailsGateBlocked,
    smsPreviewItems, treatmentTypes, therapistName, isEndMeetingSmsEnabled, smsLanguage,
    isPastDateTime, pastDateConfirmed, punchCardsEnabled, showPunchOption,
    usePunchCard, activePunchCard, seriesOverrides, clinicId, therapistId,
    currentTherapistId, overlappingAppointment, overlapConfirmed,
    canOverrideTherapistConflict, therapistConflicts, recurringConflicts,
    preventTherapistDoubleBooking, therapistAbsence, absenceConfirmed,
    canOverrideAbsence, blockBookingOnAbsence, slotChanged,
    isAdminScheduling, scheduledByAdminId, scheduledByAdminName,
    createAppointment, createRecurringAppointments, createConfirmationLink,
    updateAppointment, updateRecurringAppointments, isEditing, appointment,
    editScope, checkSMS, refreshUsageCache, therapistSmsSettings,
    onlineMeetingsEnabled,
    clinicProfile, getPatientPhone, dispatch, onClose, onSuccess,
    showSuccess, showError,
  ]);

  // ===== Blocked time (חסימת זמן) submit — fully separate from submit():
  // no SMS, no confirmation links, no payment/punch/room pipeline. =====
  const submitBlock = useCallback(async () => {
    if (isSubmitting) return;

    const errors = validateBlock(formData);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    setStepErrors({});

    const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const duration = parseInt(formData.duration, 10);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

    try {
      setIsSubmitting(true);

      if (isEditing) {
        const blockUpdateData = {
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          duration,
          blockCause: formData.blockCause?.trim() || null,
          blockComments: formData.blockComments?.trim() || null,
        };

        if (appointment?.isRecurring && !pendingEditDataRef.current) {
          pendingEditDataRef.current = blockUpdateData;
          if (!reuseEditScopeRef.current) {
            setShowScopeDialog(true);
            setIsSubmitting(false);
            return;
          }
        }

        if (appointment?.isRecurring && pendingEditDataRef.current) {
          // Blocks may overlap anything — never gate a series shift on
          // conflicts with existing meetings.
          await updateRecurringAppointments({
            clinicId,
            appointmentId: appointment.id,
            updateData: pendingEditDataRef.current,
            scope: editScope,
            patientName: formData.patientName,
            therapistId,
            therapistName,
            allowConflicts: true,
          }).unwrap();
          pendingEditDataRef.current = null;
          reuseEditScopeRef.current = false;
          showSuccess(
            editScope === "single"
              ? "החסימה עודכנה בהצלחה"
              : editScope === "all"
              ? "כל החסימות בסדרה עודכנו בהצלחה"
              : "החסימות העתידיות עודכנו בהצלחה"
          );
        } else {
          await updateAppointment({
            clinicId,
            appointmentId: appointment.id,
            appointmentData: blockUpdateData,
            therapistId,
            therapistName,
            patientName: formData.patientName,
          }).unwrap();
          showSuccess("החסימה עודכנה בהצלחה");
        }

        onClose();
        return;
      }

      const isRepeating = formData.blockRepeat !== "none";
      const result = await createBlockedTime({
        clinicId,
        therapistId,
        therapistName,
        blockData: {
          startTime: startDateTime.toISOString(),
          duration,
          blockCause: formData.blockCause?.trim() || null,
          blockComments: formData.blockComments?.trim() || null,
          repeatFrequency: isRepeating ? formData.blockRepeat : null,
          untilDate: isRepeating ? formData.blockUntilDate : null,
        },
      }).unwrap();

      showSuccess(
        result?.totalCreated > 1
          ? `${result.totalCreated} חסימות נוצרו ביומן`
          : "הזמן נחסם ביומן"
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("[CK-3815] Error saving blocked time from wizard:", {
        errorCode: "CK-3815",
        errorMessage: error?.message,
        clinicId,
        isEditing,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת החסימה");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting, formData, isEditing, appointment, editScope, clinicId,
    therapistId, therapistName, createBlockedTime, updateAppointment,
    updateRecurringAppointments, onClose, onSuccess, showSuccess, showError,
  ]);

  const allowConflictsAndResubmit = useCallback(() => {
    if (recurringConflicts.some((c) => c.selfConflict)) return;
    // Clinic policy: double-booking blocked ⇒ only admins may waive. Say so
    // instead of silently doing nothing (the panels also hide the button).
    if (!canOverrideTherapistConflict) {
      showError("מדיניות הקליניקה חוסמת כפל תורים למטפל/ת. רק מנהל/ת יכול/ה לעקוף.");
      return;
    }
    allowRecurringConflictsRef.current = true;
    // A refused series-overview row edit resumes on that row, not on the form.
    if (rowScopeEdit) {
      persistOverviewRowUpdate(rowScopeEdit.apt, {
        // A client-side row refusal remembers the scope it ran with (null =
        // single write, e.g. therapist-only change); the server refusal path
        // never stores one and keeps using the dialog's editScope.
        scope: rowScopeEdit.scope === undefined ? editScope : rowScopeEdit.scope || undefined,
        allowConflicts: true,
      });
      return;
    }
    reuseEditScopeRef.current = true;
    submit();
  }, [recurringConflicts, canOverrideTherapistConflict, rowScopeEdit, persistOverviewRowUpdate, editScope, submit, showError]);

  // Scope dialog (series edit): confirm re-enters the flow that opened it —
  // the main-form submit or a series-overview row save.
  const confirmScopeAndSubmit = useCallback(() => {
    if (isSubmitting) return;
    setShowScopeDialog(false);
    if (rowScopeEdit) {
      persistOverviewRowUpdate(rowScopeEdit.apt, { scope: editScope });
      return;
    }
    reuseEditScopeRef.current = true;
    if (isBlockMode) {
      submitBlock();
    } else {
      submit();
    }
  }, [isSubmitting, rowScopeEdit, persistOverviewRowUpdate, editScope, submit, submitBlock, isBlockMode]);

  const cancelScopeDialog = useCallback(() => {
    setShowScopeDialog(false);
    pendingEditDataRef.current = null;
    reuseEditScopeRef.current = false;
    setRowScopeEdit(null);
  }, []);

  return {
    // context
    clinicId,
    therapistId,
    clinicProfile,
    patients,
    treatmentTypes,
    canAccessFinance,
    tempCustomersEnabled,
    isEndMeetingSmsEnabled,
    therapistSmsSettings,
    clinicTherapists,
    assignableTherapists,
    isMultiTherapistPatient,
    // Picker list (assignable only); raw ids stay internal for the
    // multi-therapist detection above.
    selectedPatientTherapistIds: pickerTherapistIds,
    appointmentTherapistId,
    setAppointmentTherapistId,
    selectedPatient,
    // form
    formData,
    updateField,
    selectPatient,
    rateSource,
    durationSource,
    // steps
    step,
    maxStepReached,
    stepErrors,
    setStepErrors,
    goToStep,
    nextStep,
    prevStep,
    returnToSummary,
    // gates + checks
    isPastDateTime,
    pastDateConfirmed,
    setPastDateConfirmed,
    overlappingAppointment,
    overlapConfirmed,
    setOverlapConfirmed,
    // multi-clinic v2: therapist double-booking + absences
    therapistConflicts,
    therapistCheckPending,
    therapistCheckFailed,
    therapistAbsence,
    absenceCheckFailed,
    absenceConfirmed,
    setAbsenceConfirmed,
    canOverrideTherapistConflict,
    canOverrideAbsence,
    preventTherapistDoubleBooking,
    blockBookingOnAbsence,
    therapistName,
    overlappingBlock,
    // Edit: slot untouched ⇒ conflict/absence panel is information only
    slotChanged,
    therapistGateInformational,
    noRoomConfirmed,
    setNoRoomConfirmed,
    zeroAmountConfirmed,
    setZeroAmountConfirmed,
    needsAmountGate,
    scheduleGateBlocked,
    detailsGateBlocked,
    recurringConflicts,
    allowConflictsAndResubmit,
    // rooms
    roomsEnabled,
    accessibleRooms,
    roomConflicts,
    roomAvailabilityError,
    roomAvailabilityReason,
    roomCheckPending,
    seriesRoomConflicts,
    seriesRoomCheckPending,
    seriesEditRoomWarnings,
    roomOverrideConfirmed,
    setRoomOverrideConfirmed,
    isAdmin,
    canManageOthersAppointments,
    canScheduleForOthers,
    // E6: patient ↔ therapist assignment gate
    allowMultipleTherapists,
    unassignedTherapistForPatient,
    hasUnassignedTherapist,
    isPatientUnassignedBlocked,
    unassignedAcknowledged,
    setUnassignedAcknowledged,
    canAcknowledgeUnassigned,
    canAssignTherapistToPatient,
    assignTherapistToPatient,
    isAssigningTherapist,
    seriesUnassignedIdx,
    therapistDisplayName,
    // series
    seriesPreview,
    seriesOverrides,
    setSeriesOverrides,
    // online video meeting
    onlineMeetingsEnabled,
    // punch card
    punchCardsEnabled,
    showPunchOption,
    usePunchCard,
    handlePunchToggle,
    activePunchCard,
    remainingPunches,
    punchAmountForCard,
    rateDisabled,
    // SMS preview
    smsPreviewItems,
    toggleSmsPreviewItem,
    setAllSmsEnabled,
    updateSmsPreviewItem,
    getPatientPhone,
    // fast path
    fastPathReady,
    goToSummaryFastPath,
    // blocked time
    isBlockMode,
    submitBlock,
    // submit / close
    submit,
    isSubmitting:
      isSubmitting ||
      isCreating ||
      isCreatingRecurring ||
      isCreatingBlock ||
      isUpdating ||
      isUpdatingRecurring,
    smsError,
    isFormDirty,
    handleSafeClose,
    showCloseWarning,
    setShowCloseWarning,
    onClose,
    PlanLimitModalComponent,
    // ===== Edit mode =====
    isEditing,
    appointment,
    liveAppointment,
    seriesAppointments,
    refetchSeries,
    cancelFlowNode,
    startCancel,
    startPostpone,
    handleRestoreAppointment,
    isRestoring,
    // delete
    handleDeleteClick,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteDialogWorking,
    isDeleting,
    isDeletingRecurring,
    handleDeleteConfirm,
    deleteScope,
    setDeleteScope,
    showRecurringDeleteDialog,
    setShowRecurringDeleteDialog,
    recurringDeleteState,
    setRecurringDeleteState,
    handleRecurringDeleteConfirm,
    // scope dialog
    editScope,
    setEditScope,
    showScopeDialog,
    confirmScopeAndSubmit,
    cancelScopeDialog,
    isUpdatingRecurring,
    scopeNewStart,
    laterSeriesMeetings,
    laterShiftedMeetings,
    postponableMeetings,
    canPostponeSeries,
    canPostponeAppointment,
    postponedNextMeetingDate,
    postponedLastMeetingDate,
    // series overview (2b)
    canReassignSeriesTherapist,
    isAppointmentPastOrFinalized,
    editingOverviewRow,
    overviewEditValues,
    setOverviewEditValues,
    handleOverviewRowEdit,
    cancelOverviewRowEdit,
    handleOverviewRowSave,
    handleSeriesStatusChange,
    seriesCancelPrompt,
    setSeriesCancelPrompt,
    handleSeriesCancelConfirm,
    pastReassignConfirm,
    setPastReassignConfirm,
    persistOverviewRowUpdate,
    restoringRowId,
    rowScopeEdit,
    recurringConflictsForRows: recurringConflicts,
    // communication (2c)
    smsAppointment,
    confirmationStatus,
    noSmsRequested,
    confirmationLinks,
    canCancelScheduledSms,
    cancelSmsTarget,
    setCancelSmsTarget,
    cancellingSmsType,
    handleConfirmCancelScheduledSms,
    handleCreateLink,
    copyLink,
    copiedLinkId,
    canSendLateSms,
    lateSmsSeriesAvailable,
    lateSmsFutureOccurrences,
    lateSmsReminderOffered,
    lateSmsPreviewText,
    showLateSmsDialog,
    setShowLateSmsDialog,
    // manual reminder now
    canSendReminderNow,
    reminderNowAutoPending,
    reminderNowLastSentAt,
    showReminderNowDialog,
    setShowReminderNowDialog,
    reminderNowSending,
    handleSendReminderNow,
    lateSmsScope,
    setLateSmsScope,
    lateSmsIncludeReminder,
    setLateSmsIncludeReminder,
    lateSmsSending,
    handleLateSmsSend,
  };
}
